// ============================================
// API Client for StageFlow → Supabase Backend
// ============================================

import { projectId, publicAnonKey } from '/utils/supabase/info';
import { reportApiError } from './report-error';
import type { CalendarEvent } from './mockData';
import type { BusyEvent } from './autoSchedule';

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-43ef46c1/api`;

const headers = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${publicAnonKey}`,
});

// Retry wrapper for transient network failures (TypeError: Failed to fetch)
// Uses exponential backoff: 1s, 2s, 4s between retries
async function fetchWithRetry(input: RequestInfo | URL, init?: RequestInit, retries = 3): Promise<Response> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetch(input, init);
    } catch (err) {
      if (attempt === retries) {
        // Final attempt failed — the request never reached the server.
        // Report it as a network error (status 0) before surfacing to callers.
        const reqUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
        reportApiError(0, reqUrl, err?.message);
        throw err;
      }
      const delay = 1000 * Math.pow(2, attempt); // 1s, 2s, 4s
      await new Promise(r => setTimeout(r, delay));
      const route = typeof input === 'string' ? input.split('/api/')[1] : 'request';
      console.warn(`Fetch retry ${attempt + 1}/${retries} for ${route} (next in ${delay}ms)`);
    }
  }
  throw new Error('Unreachable');
}

/** Error carrying the structured detail a route returned alongside its message. */
export interface ApiError extends Error {
  status?: number;
  /** Per-item problems, when the route reports a list rather than one message. */
  details?: string[];
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    console.error(`API error [${res.status}]:`, body);
    // The message alone is not always enough: routes that validate several
    // things at once (the Canva field mapping, for one) return a `details`
    // list naming each problem, and throwing only `error` would drop exactly
    // the part the user needs to act on.
    const err = new Error(body.error || `Request failed with status ${res.status}`) as ApiError;
    err.status = res.status;
    if (Array.isArray(body.details)) err.details = body.details;
    // Report server-side failures (5xx) to the central portal. reportApiError
    // filters out 4xx internally, so client errors like 401/404 are skipped.
    reportApiError(res.status, res.url, body?.error);
    throw err;
  }
  return res.json();
}

// ============================================
// SYSTEM TIMEZONE
// ============================================

export async function getSystemTimezone(): Promise<{ timezone: string }> {
  const res = await fetchWithRetry(`${BASE}/system/timezone`, { headers: headers() });
  return handleResponse(res);
}

export async function setSystemTimezone(timezone: string): Promise<{ timezone: string; success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/system/timezone`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify({ timezone }),
  });
  return handleResponse(res);
}

// Org-wide default scheduling window used by Plan with AI (per-plan overridable).
export interface WorkHours {
  workdayStart: string;   // "HH:MM"
  workdayEnd: string;     // "HH:MM"
  workingDays: number[];  // 0=Sunday .. 6=Saturday
}

export async function getWorkHours(): Promise<WorkHours> {
  const res = await fetchWithRetry(`${BASE}/system/work-hours`, { headers: headers() });
  return handleResponse(res);
}

// Writes are admin-gated server-side (same posture as GA4 settings); reads
// stay on the anon key because every signed-in client seeds the scheduler.
export async function setWorkHours(accessToken: string, hours: WorkHours): Promise<WorkHours & { success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/system/work-hours`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(hours),
  });
  return handleResponse(res);
}

// ============================================
// GOOGLE ANALYTICS 4
// ============================================
// Admin Settings is the source of truth. Public pages read the same
// three fields (Measurement ID is not a secret) so gtag can load.
// GET/PUT /api/system/ga4 require a signed-in admin JWT — the public
// anon key is rejected (same gate as integration credentials).

import type { Ga4Settings } from './ga4';

export async function getGa4Settings(accessToken: string): Promise<Ga4Settings> {
  const res = await fetchWithRetry(`${BASE}/system/ga4`, { headers: authHeaders(accessToken) });
  return handleResponse(res);
}

export async function saveGa4Settings(accessToken: string, data: Ga4Settings): Promise<Ga4Settings> {
  const res = await fetchWithRetry(`${BASE}/system/ga4`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify({
      ga_measurement_id: data.ga_measurement_id,
      ga_stream_id: data.ga_stream_id,
      ga_property_id: data.ga_property_id,
    }),
  });
  return handleResponse(res);
}

export async function getPublicSiteConfig(): Promise<Ga4Settings> {
  const res = await fetchWithRetry(`${BASE}/public/site-config`, { headers: headers() });
  return handleResponse(res);
}

// Seed the database with initial data (idempotent)
export async function seed(): Promise<{ seeded: boolean; count?: number }> {
  const res = await fetchWithRetry(`${BASE}/seed`, { method: 'POST', headers: headers(), body: JSON.stringify({}) });
  return handleResponse(res);
}

// Fetch everything in one call
export async function fetchAll(): Promise<Record<string, any[]>> {
  const res = await fetchWithRetry(`${BASE}/all`, { headers: headers() });
  return handleResponse(res);
}

// Fetch all items for a collection
export async function fetchCollection<T>(collection: string): Promise<T[]> {
  const res = await fetchWithRetry(`${BASE}/${collection}`, { headers: headers() });
  return handleResponse(res);
}

// Fetch single item
export async function fetchItem<T>(collection: string, id: string): Promise<T> {
  const res = await fetchWithRetry(`${BASE}/${collection}/${id}`, { headers: headers() });
  return handleResponse(res);
}

// Fetch single item, returning null if it doesn't exist (404) — use for optional config entries
export async function fetchItemOptional<T>(collection: string, id: string): Promise<T | null> {
  const res = await fetchWithRetry(`${BASE}/${collection}/${id}`, { headers: headers() });
  if (res.status === 404) return null;
  return handleResponse(res);
}

// Create a new item
export async function createItem<T>(collection: string, data: Partial<T>): Promise<T> {
  const res = await fetchWithRetry(`${BASE}/${collection}`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

// Update an item
export async function updateItem<T>(collection: string, id: string, data: Partial<T>): Promise<T> {
  const res = await fetchWithRetry(`${BASE}/${collection}/${id}`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

// Delete an item
export async function deleteItem(collection: string, id: string): Promise<void> {
  const res = await fetchWithRetry(`${BASE}/${collection}/${id}`, {
    method: 'DELETE',
    headers: headers(),
  });
  await handleResponse(res);
}

// Force reseed
export async function reseed(): Promise<{ count: number }> {
  const res = await fetchWithRetry(`${BASE}/reseed`, { method: 'POST', headers: headers() });
  return handleResponse(res);
}

// Fetch admin stats for SuperAdmin dashboard
export async function fetchAdminStats(): Promise<any> {
  const res = await fetchWithRetry(`${BASE}/admin/stats`, { headers: headers() });
  return handleResponse(res);
}

// ============================================
// CHECK-IN API
// ============================================

import type { CheckIn, CheckInLocation } from './mockData';

export interface KioskRosterEntry {
  id: string;
  name: string;
  avatarColor: string | null;
  avatarUrl: string | null;
  hasPin: boolean;
}

export interface ActiveCheckIn {
  id: string;
  teamMemberId: string;
  teamMemberName: string;
  avatarColor: string | null;
  location: CheckInLocation;
  checkedInAt: string;
  method: 'kiosk' | 'self' | 'admin';
}

export interface CheckInToggleResult {
  record: CheckIn;
  action: 'checked-in' | 'checked-out';
}

function authHeaders(token: string) {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

export async function fetchKioskRoster(): Promise<KioskRosterEntry[]> {
  const res = await fetchWithRetry(`${BASE}/checkin/kiosk-roster`, { headers: headers() });
  return handleResponse(res);
}

export async function fetchActiveCheckIns(location?: CheckInLocation): Promise<ActiveCheckIn[]> {
  const qs = location ? `?location=${encodeURIComponent(location)}` : '';
  const res = await fetchWithRetry(`${BASE}/checkin/active${qs}`, { headers: headers() });
  return handleResponse(res);
}

export async function kioskCheckIn(
  teamMemberId: string,
  pin: string,
  location: CheckInLocation = 'office',
): Promise<CheckInToggleResult> {
  const res = await fetchWithRetry(`${BASE}/checkin/kiosk`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ teamMemberId, pin, location }),
  });
  return handleResponse(res);
}

export async function selfCheckIn(
  token: string,
  location: CheckInLocation = 'office',
  note?: string,
): Promise<CheckInToggleResult> {
  const res = await fetchWithRetry(`${BASE}/checkin/self`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ location, note }),
  });
  return handleResponse(res);
}

export async function fetchMyCheckInStatus(token: string): Promise<{ open: CheckIn | null }> {
  const res = await fetchWithRetry(`${BASE}/checkin/my-status`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export async function fetchCheckInHistory(
  token: string,
  opts: { from?: string; to?: string; teamMemberId?: string } = {},
): Promise<CheckIn[]> {
  const params = new URLSearchParams();
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  if (opts.teamMemberId) params.set('teamMemberId', opts.teamMemberId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetchWithRetry(`${BASE}/checkin/history${qs}`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export interface SessionEditInput {
  checkedInAt?: string;           // ISO
  checkedOutAt?: string | null;   // ISO, null to clear (reopen)
  location?: CheckInLocation;
  note?: string;
}

export async function updateCheckInSession(
  token: string,
  sessionId: string,
  patch: SessionEditInput,
): Promise<{ record: CheckIn }> {
  const res = await fetchWithRetry(`${BASE}/checkin/session/${encodeURIComponent(sessionId)}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(patch),
  });
  return handleResponse(res);
}

export interface SessionCreateInput {
  teamMemberId: string;
  checkedInAt: string;    // ISO
  checkedOutAt: string;   // ISO
  location: CheckInLocation;
  note?: string;
}

export async function createCheckInSession(
  token: string,
  input: SessionCreateInput,
): Promise<{ record: CheckIn }> {
  const res = await fetchWithRetry(`${BASE}/checkin/session`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(input),
  });
  return handleResponse(res);
}

export async function deleteCheckInSession(
  token: string,
  sessionId: string,
): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/checkin/session/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function setMemberPin(
  token: string,
  teamMemberId: string,
  pin: string | null,
): Promise<{ success: boolean; hasPin: boolean }> {
  const res = await fetchWithRetry(`${BASE}/checkin/set-pin`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ teamMemberId, pin }),
  });
  return handleResponse(res);
}

// ============================================
// VOICE COMMAND API
// ============================================

export interface VoiceCommandContext {
  projectNames: string[];
  deliverableNames: string[];
  teamMemberNames: string[];
  currentPage: string;
  inFocusMode: boolean;
  currentFocusTask?: string;
}

export interface VoiceCommandResult {
  action: string | null;
  params?: Record<string, any>;
  message?: string;
  transcript: string;
  confidence: 'high' | 'none';
}

export async function parseVoiceCommand(
  transcript: string,
  context: VoiceCommandContext,
): Promise<VoiceCommandResult> {
  const res = await fetchWithRetry(`${BASE}/voice/parse`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ transcript, context }),
  });
  return handleResponse(res);
}

// ============================================
// FILE ATTACHMENTS API
// ============================================

// The three surfaces that can carry a file attachment
export type AttachmentTargetType = 'deliverable' | 'content' | 'merch';

export interface FileAttachment {
  id: string;
  // New generalized target — every file belongs to one of: deliverable | content | merch
  targetType: AttachmentTargetType;
  targetId: string;
  // Kept for backwards compat (== targetId when targetType === 'deliverable')
  deliverableId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  storagePath: string;
  label: string;
  version: number;
  isLatest: boolean;
  replaces: string | null;
  replacedBy: string | null;
  uploadedBy: string;
  uploadedAt: string;
  signedUrl: string | null;
  // Linked file support (external URLs, Canva, Dropbox, Google Drive, etc.)
  source: 'upload' | 'canva' | 'dropbox' | 'airtable' | 'google_drive' | 'url';
  externalUrl: string | null;
  externalId: string | null;
  thumbnailUrl: string | null;
}

// Upload a file to any target (deliverable | content | merch)
export async function uploadTargetFile(
  file: File,
  targetType: AttachmentTargetType,
  targetId: string,
  label: string,
  uploaderName: string,
  replaceFileId?: string,
): Promise<FileAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('targetType', targetType);
  formData.append('targetId', targetId);
  // Also send legacy deliverableId for backwards compat with older server code paths
  if (targetType === 'deliverable') formData.append('deliverableId', targetId);
  formData.append('label', label);
  formData.append('uploaderName', uploaderName);
  if (replaceFileId) formData.append('replaceFileId', replaceFileId);

  const res = await fetchWithRetry(`${BASE}/files/upload`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${publicAnonKey}` },
    body: formData,
  });
  return handleResponse(res);
}

// Legacy: upload a file to a deliverable
export async function uploadFile(
  file: File,
  deliverableId: string,
  label: string,
  uploaderName: string,
  replaceFileId?: string,
): Promise<FileAttachment> {
  return uploadTargetFile(file, 'deliverable', deliverableId, label, uploaderName, replaceFileId);
}

// List files for any target (deliverable | content | merch)
export async function fetchTargetFiles(
  targetType: AttachmentTargetType,
  targetId: string,
): Promise<FileAttachment[]> {
  const res = await fetchWithRetry(`${BASE}/files/target/${targetType}/${targetId}`, { headers: headers() });
  return handleResponse(res);
}

// List files for a deliverable (legacy wrapper)
export async function fetchDeliverableFiles(deliverableId: string): Promise<FileAttachment[]> {
  const res = await fetchWithRetry(`${BASE}/files/deliverable/${deliverableId}`, { headers: headers() });
  return handleResponse(res);
}

// Get shareable link (7-day signed URL)
export async function getShareLink(fileId: string): Promise<{ signedUrl: string; expiresIn: string }> {
  const res = await fetchWithRetry(`${BASE}/files/${fileId}/share`, { headers: headers() });
  return handleResponse(res);
}

// Update file metadata
export async function updateFileMetadata(fileId: string, updates: Partial<FileAttachment>): Promise<FileAttachment> {
  const res = await fetchWithRetry(`${BASE}/files/${fileId}`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(updates),
  });
  return handleResponse(res);
}

// Delete a file
export async function deleteFile(fileId: string): Promise<void> {
  const res = await fetchWithRetry(`${BASE}/files/${fileId}`, {
    method: 'DELETE',
    headers: headers(),
  });
  await handleResponse(res);
}

// File counts per deliverable (lightweight, no signed URLs)
export async function fetchFileCounts(): Promise<Record<string, number>> {
  const res = await fetchWithRetry(`${BASE}/files/counts`, { headers: headers() });
  return handleResponse(res);
}

// ============================================
// GOOGLE DRIVE PICKER CONFIG
// Public-safe credentials (API key + OAuth client ID) so the browser
// picker can authenticate. These are inherently safe to expose:
// - the API key is restricted by referrer in Google Cloud Console
// - the OAuth client ID is embedded in every auth flow anyway
// ============================================

export interface GoogleDrivePublicConfig {
  apiKey: string | null;
  clientId: string | null;
  appId: string | null;
  // When set, the picker is locked to this folder — users can only browse
  // and pick files inside it (and its subfolders).
  folderId: string | null;
  hasCredentials: boolean;
}

export async function getGoogleDrivePublicConfig(): Promise<GoogleDrivePublicConfig> {
  try {
    const res = await fetchWithRetry(`${BASE}/integrations/google-drive/public-config`, {
      headers: headers(),
    });
    return handleResponse(res);
  } catch {
    return { apiKey: null, clientId: null, appId: null, folderId: null, hasCredentials: false };
  }
}

export interface GoogleDriveOAuthStatus {
  hasClientId: boolean;
  hasClientSecret: boolean;
  connected: boolean;
  email: string | null;
  connectedAt: string | null;
  expiresAt: number | null;
}

export async function getGoogleDriveStatus(): Promise<GoogleDriveOAuthStatus> {
  try {
    const res = await fetchWithRetry(`${BASE}/gdrive/status`, { headers: headers() });
    return handleResponse(res);
  } catch {
    return { hasClientId: false, hasClientSecret: false, connected: false, email: null, connectedAt: null, expiresAt: null };
  }
}

export async function getGoogleDriveAuthUrl(): Promise<{ authUrl: string; redirectUri: string }> {
  const res = await fetchWithRetry(`${BASE}/gdrive/auth-url`, { headers: headers() });
  return handleResponse(res);
}

export async function disconnectGoogleDrive(): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/gdrive/disconnect`, {
    method: 'POST',
    headers: headers(),
  });
  return handleResponse(res);
}

// Redirect URI the user must add in Google Cloud Console → Credentials.
// Computed client-side from the same BASE so it never drifts from what the server actually uses.
export const googleDriveRedirectUri = `${BASE}/gdrive/callback`;

// Link an external URL as a file attachment to any target (deliverable | content | merch)
export async function createTargetLinkedFile(data: {
  targetType: AttachmentTargetType;
  targetId: string;
  url: string;
  fileName: string;
  label: string;
  uploaderName: string;
  source?: 'url' | 'canva' | 'dropbox' | 'airtable' | 'google_drive';
  externalId?: string;
  thumbnailUrl?: string;
  fileType?: string;
}): Promise<FileAttachment> {
  const payload: any = { ...data };
  // Mirror to deliverableId for backwards compat when targeting deliverables
  if (data.targetType === 'deliverable') payload.deliverableId = data.targetId;
  const res = await fetchWithRetry(`${BASE}/files/link`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// Legacy: link an external URL to a deliverable
export async function createLinkedFile(data: {
  deliverableId: string;
  url: string;
  fileName: string;
  label: string;
  uploaderName: string;
  source?: 'url' | 'canva' | 'dropbox' | 'airtable' | 'google_drive';
  externalId?: string;
  thumbnailUrl?: string;
  fileType?: string;
}): Promise<FileAttachment> {
  const { deliverableId, ...rest } = data;
  return createTargetLinkedFile({
    ...rest,
    targetType: 'deliverable',
    targetId: deliverableId,
  });
}

// ============================================
// INBOX COUNTS (lightweight, for sidebar badge polling)
// ============================================

export interface InboxCounts {
  submissions: number;
  interests: number;
  total: number;
}

export async function fetchInboxCounts(): Promise<InboxCounts> {
  const res = await fetchWithRetry(`${BASE}/inbox/counts`, { headers: headers() });
  return handleResponse(res);
}

// ============================================
// COMMUNITY POLLS API
// ============================================

export async function fetchActivePolls(): Promise<any[]> {
  const res = await fetchWithRetry(`${BASE}/polls/active`, { headers: headers() });
  return handleResponse(res);
}

export async function submitPollVote(pollId: string, data: {
  selectedOptionIds: string[];
  voterName?: string;
  voterEmail?: string;
  voterFingerprint?: string;
  customOptionText?: string;
}): Promise<{ success: boolean; voteId: string }> {
  const res = await fetchWithRetry(`${BASE}/polls/${pollId}/vote`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export interface ExternalPollResult {
  pollId: string;
  source: string;
  session_id: string;
  session_name: string | null;
  performance_date: string | null;
  total_responses: number;
  option_counts: Record<string, number>;
  custom_responses: string[];
  synced_at: string;
}

/** Aggregate snapshots pushed by connected apps (FairDraw Live). */
export async function fetchExternalPollResults(pollId: string): Promise<ExternalPollResult[]> {
  const res = await fetchWithRetry(`${BASE}/polls/${pollId}/external-results`, { headers: headers() });
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({ results: [] }));
  return data.results ?? [];
}

export interface ExternalKeyMeta {
  label: string;
  key_prefix: string;
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
  hash: string;
}

export async function listExternalKeys(): Promise<ExternalKeyMeta[]> {
  const res = await fetchWithRetry(`${BASE}/external-keys`, { headers: headers() });
  if (!res.ok) throw new Error('Failed to list external keys');
  return (await res.json()).keys ?? [];
}

export async function mintExternalKey(label: string): Promise<{ raw_key: string; meta: ExternalKeyMeta }> {
  const res = await fetchWithRetry(`${BASE}/external-keys`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ label }),
  });
  if (!res.ok) throw new Error('Failed to mint key');
  return res.json();
}

export async function revokeExternalKey(hash: string): Promise<void> {
  const res = await fetchWithRetry(`${BASE}/external-keys/${hash}/revoke`, {
    method: 'POST',
    headers: headers(),
  });
  if (!res.ok) throw new Error('Failed to revoke key');
}

export async function fetchPollResults(pollId: string): Promise<{
  pollId: string;
  totalVotes: number;
  optionCounts: Record<string, number>;
  customResponses: string[];
}> {
  const res = await fetchWithRetry(`${BASE}/polls/${pollId}/results`, { headers: headers() });
  return handleResponse(res);
}

// ============================================
// MENTION NOTIFICATIONS API
// ============================================

export interface MentionNotification {
  id: string;
  recipientName: string;
  recipientMemberId: string;
  senderName: string;
  senderColor: string;
  deliverableId: string;
  deliverableTitle: string;
  messagePreview: string;
  createdAt: string;
  read: boolean;
  /** Flavor — drives the wording in the bell dropdown. Absent means 'mention'. */
  type?: 'mention' | 'handoff' | 'agenda';
  /** Where clicking navigates. Falls back to the linked deliverable. */
  link?: string;
}

export async function fetchNotifications(recipientName: string): Promise<MentionNotification[]> {
  const res = await fetchWithRetry(`${BASE}/notifications`, { headers: headers() });
  const all: MentionNotification[] = await handleResponse(res);
  return all.filter(n => n.recipientName === recipientName);
}

export async function markNotificationRead(id: string): Promise<void> {
  const res = await fetchWithRetry(`${BASE}/notifications/${id}`, { headers: headers() });
  const existing: MentionNotification = await handleResponse(res);
  await fetchWithRetry(`${BASE}/notifications/${id}`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify({ ...existing, read: true }),
  });
}

export async function markAllNotificationsRead(recipientName: string): Promise<void> {
  const res = await fetchWithRetry(`${BASE}/notifications`, { headers: headers() });
  const all: MentionNotification[] = await handleResponse(res);
  const mine = all.filter(n => n.recipientName === recipientName && !n.read);
  await Promise.all(mine.map(n =>
    fetchWithRetry(`${BASE}/notifications/${n.id}`, {
      method: 'PUT',
      headers: headers(),
      body: JSON.stringify({ ...n, read: true }),
    })
  ));
}

export async function createNotification(data: MentionNotification): Promise<MentionNotification> {
  const res = await fetchWithRetry(`${BASE}/notifications`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

// ============================================
// HANDOFF API
// ============================================

import type { HandoffEvent, DeliverableStatus } from './mockData';

export interface HandoffPayload {
  deliverableId: string;
  fromUser: string;
  toUser: string;
  toType: 'user' | 'department';
  newStatus: DeliverableStatus;
  note: string;
  dueDate?: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  notifyRecipient: boolean;
}

export async function executeHandoff(payload: HandoffPayload): Promise<{ handoff: HandoffEvent; deliverable: any }> {
  const res = await fetchWithRetry(`${BASE}/handoffs/execute`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function fetchHandoffsByDeliverable(deliverableId: string): Promise<HandoffEvent[]> {
  const res = await fetchWithRetry(`${BASE}/handoffs/by-deliverable/${deliverableId}`, { headers: headers() });
  return handleResponse(res);
}

export async function fetchHandoffsByUser(userName: string): Promise<HandoffEvent[]> {
  const res = await fetchWithRetry(`${BASE}/handoffs/by-user/${encodeURIComponent(userName)}`, { headers: headers() });
  return handleResponse(res);
}

// ============================================
// INTEGRATIONS API
// ============================================

export interface Integration {
  id: string;
  name: string;
  envKey: string;
  description: string;
  category: string;
  status: 'connected' | 'disconnected' | 'error';
  connectedAt: string | null;
  lastTestedAt: string | null;
  lastTestResult: string | null;
  hasApiKey: boolean;
  maskedKey: string | null;
  /** 'oauth' for integrations connected by grant rather than a stored key. */
  credentialSource: 'env' | 'settings' | 'oauth' | null;
  config: Record<string, any>;
}

export interface IntegrationTestResult {
  success: boolean;
  status: string;
  message: string;
  testedAt: string;
}

// Fetch all integrations
export async function fetchIntegrations(): Promise<Integration[]> {
  const res = await fetchWithRetry(`${BASE}/integrations`, { headers: headers() });
  return handleResponse(res);
}

// Fetch single integration
export async function fetchIntegration(id: string): Promise<Integration> {
  const res = await fetchWithRetry(`${BASE}/integrations/${id}`, { headers: headers() });
  return handleResponse(res);
}

// Update integration config
export async function updateIntegration(
  token: string,
  id: string,
  data: Partial<Integration>,
): Promise<Integration> {
  const res = await fetchWithRetry(`${BASE}/integrations/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

// Test integration connection
export async function testIntegration(token: string, id: string): Promise<IntegrationTestResult> {
  const res = await fetchWithRetry(`${BASE}/integrations/${id}/test`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ============================================
// CREDENTIALS API (store API keys via UI)
// ============================================
// Writes here take the caller's access token: the server admits only signed-in
// admins. Storing, replacing, and deleting an integration credential used to be
// reachable with the public anon key.

export interface CredentialSaveResult {
  success: boolean;
  maskedKey: string;
  savedAt: string;
}

// Save a credential (API key / token) with optional extra fields
export async function saveCredential(
  accessToken: string,
  integrationId: string,
  token: string,
  label?: string,
  extra?: Record<string, unknown>,
): Promise<CredentialSaveResult> {
  const res = await fetchWithRetry(`${BASE}/integrations/${integrationId}/credentials`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ token, label, ...extra }),
  });
  return handleResponse(res);
}

// ============================================
// AIRTABLE API
// ============================================

export interface AirtableBase {
  id: string;
  name: string;
}

export interface AirtableField {
  id: string;
  name: string;
  type: string;
}

export interface AirtableTable {
  id: string;
  name: string;
  fields: AirtableField[];
}

export interface AirtableImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export interface AirtableSyncResult {
  pulled: number;
  pushed: number;
  errors: string[];
}

// List Airtable bases accessible to the stored token
export async function fetchAirtableBases(): Promise<{ bases: AirtableBase[] }> {
  const res = await fetchWithRetry(`${BASE}/airtable/bases`, { headers: headers() });
  return handleResponse(res);
}

// Fetch table schema for a specific base
export async function fetchAirtableTableSchema(baseId: string): Promise<{ tables: AirtableTable[] }> {
  const res = await fetchWithRetry(`${BASE}/airtable/bases/${baseId}/tables`, { headers: headers() });
  return handleResponse(res);
}

// Save Airtable-specific config (baseId, tableName, fieldMapping, targetProjectId)
// Pass token if changing it at the same time; otherwise pass the existing masked token
export async function saveAirtableConfig(config: {
  token: string;
  baseId?: string;
  tableName?: string;
  fieldMapping?: Record<string, string>;
  targetProjectId?: string;
}): Promise<CredentialSaveResult> {
  const { token, ...extra } = config;
  return saveCredential('airtable', token, undefined, extra);
}

// Import records from Airtable → StageFlow deliverables
export async function importFromAirtable(targetProjectId?: string): Promise<AirtableImportResult> {
  const res = await fetchWithRetry(`${BASE}/airtable/import`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(targetProjectId ? { targetProjectId } : {}),
  });
  return handleResponse(res);
}

// Sync between Airtable and StageFlow
export async function syncAirtable(direction: 'pull' | 'push' | 'both'): Promise<AirtableSyncResult> {
  const res = await fetchWithRetry(`${BASE}/airtable/sync`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ direction }),
  });
  return handleResponse(res);
}

// Deduplicate deliverables (remove duplicates created by Airtable re-import)
export async function dedupDeliverables(): Promise<{
  success: boolean;
  removed: number;
  kept: number;
  groups: number;
  rebuilt: number;
  details: string[];
  totalRemaining: number;
}> {
  const res = await fetchWithRetry(`${BASE}/admin/dedup-deliverables`, {
    method: 'POST',
    headers: headers(),
  });
  return handleResponse(res);
}

// ============================================
// AIRTABLE MULTI-ENTITY API
// ============================================

export type AirtableEntityType = 'deliverables' | 'projects' | 'events' | 'venues' | 'merch' | 'social-posts';

export interface AirtableEntityConfig {
  baseId: string;
  tableName: string;
  fieldMapping: Record<string, string>;
  targetProjectId?: string;
  dateFilterFrom?: string;
  dateFilterTo?: string;
  savedAt?: string;
}

// Save per-entity-type Airtable config
export async function saveAirtableEntityConfig(
  entityType: AirtableEntityType,
  config: Omit<AirtableEntityConfig, 'savedAt'>,
): Promise<{ ok: boolean }> {
  const res = await fetchWithRetry(`${BASE}/airtable/config/${entityType}`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(config),
  });
  return handleResponse(res);
}

// Fetch saved per-entity-type Airtable config
export async function fetchAirtableEntityConfig(
  entityType: AirtableEntityType,
): Promise<AirtableEntityConfig | null> {
  const res = await fetchWithRetry(`${BASE}/airtable/config/${entityType}`, { headers: headers() });
  const data = await handleResponse(res) as { config: AirtableEntityConfig | null };
  return data.config;
}

// Import records from Airtable for a specific entity type
export async function importFromAirtableEntity(
  entityType: AirtableEntityType,
  options?: { targetProjectId?: string; dateFilterFrom?: string; dateFilterTo?: string },
): Promise<AirtableImportResult> {
  const res = await fetchWithRetry(`${BASE}/airtable/import`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ entityType, ...options }),
  });
  return handleResponse(res);
}

// Sync Airtable for a specific entity type
export async function syncAirtableEntity(
  entityType: AirtableEntityType,
  direction: 'pull' | 'push' | 'both',
): Promise<AirtableSyncResult> {
  const res = await fetchWithRetry(`${BASE}/airtable/sync`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ entityType, direction }),
  });
  return handleResponse(res);
}

// Remove a stored credential
export async function removeCredential(accessToken: string, integrationId: string): Promise<void> {
  const res = await fetchWithRetry(`${BASE}/integrations/${integrationId}/credentials`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  await handleResponse(res);
}

// ============================================
// GOOGLE CALENDAR API (MULTI-CONNECTION SUPPORT)
// ============================================

export interface GCalConnection {
  id: string;
  email: string;
  selectedCalendarIds: string[];
  selectedCalendars: Array<{
    id: string;
    summary: string;
    backgroundColor?: string;
  }>;
  connectedAt: string;
  lastSyncAt: string | null;
}

export interface GCalStatus {
  hasCredentials: boolean;
  isConnected: boolean;
  hasRefreshToken: boolean;
  selectedCalendarId: string | null;
  selectedCalendarName: string | null;
  lastSyncAt: string | null;
  syncDirection: 'pull' | 'push' | 'both';
  connectedEmail: string | null;
  // New multi-connection fields
  connectionCount: number;
  connections: Array<{
    id: string;
    email: string;
    selectedCalendarIds: string[];
    lastSyncAt: string | null;
  }>;
}

export interface GCalCalendar {
  id: string;
  summary: string;
  description: string;
  primary: boolean;
  backgroundColor: string;
  accessRole: string;
}

export interface GCalSyncResult {
  success: boolean;
  created: number;
  updated: number;
  skipped?: number;
  failed?: number;
  total: number;
}

// Get Google Calendar connection status
export async function getGCalStatus(): Promise<GCalStatus> {
  const res = await fetchWithRetry(`${BASE}/gcal/status`, { headers: headers() });
  return handleResponse(res);
}

// List all Google Calendar connections
export async function getGCalConnections(): Promise<{ connections: GCalConnection[] }> {
  const res = await fetchWithRetry(`${BASE}/gcal/connections`, { headers: headers() });
  return handleResponse(res);
}

// Get specific connection
export async function getGCalConnection(id: string): Promise<GCalConnection> {
  const res = await fetchWithRetry(`${BASE}/gcal/connections/${id}`, { headers: headers() });
  return handleResponse(res);
}

// List calendars for a specific connection
export async function getGCalCalendarsForConnection(connectionId: string): Promise<{ calendars: GCalCalendar[] }> {
  const res = await fetchWithRetry(`${BASE}/gcal/connections/${connectionId}/calendars`, { headers: headers() });
  return handleResponse(res);
}

// Update selected calendars for a connection
export async function updateGCalConnectionCalendars(
  connectionId: string,
  selectedCalendarIds: string[],
  selectedCalendars: Array<{ id: string; summary: string; backgroundColor?: string }>
): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/gcal/connections/${connectionId}/calendars`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify({ selectedCalendarIds, selectedCalendars }),
  });
  return handleResponse(res);
}

// Disconnect a specific connection
export async function disconnectGCalConnection(connectionId: string): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/gcal/connections/${connectionId}/disconnect`, {
    method: 'POST',
    headers: headers(),
  });
  return handleResponse(res);
}

// Get the OAuth authorization URL to connect Google Calendar. The route is now
// gated (requireTeamMember), so a signed-in user's token is required. kind
// 'personal' connects the caller's OWN account (default); 'org' connects the
// shared org calendar and is admin-only (enforced server-side).
export async function getGCalAuthUrl(
  accessToken: string,
  kind: 'personal' | 'org' = 'personal',
): Promise<{ authUrl: string; redirectUri: string }> {
  const res = await fetchWithRetry(`${BASE}/gcal/auth-url?kind=${kind}`, { headers: authHeaders(accessToken) });
  return handleResponse(res);
}

// ---- Personal (per-member) Google Calendar + availability ----
// These hit the requireTeamMember-gated routes, so they take the user's token.

// Import the caller's Google events into their private calendar (Postgres).
export async function pullMyGCal(accessToken: string): Promise<{ success: boolean; synced: number; skipped: number }> {
  const res = await fetchWithRetry(`${BASE}/gcal/my/pull`, { method: 'POST', headers: authHeaders(accessToken) });
  return handleResponse(res);
}

// Push the caller's auto-scheduled work-blocks to their own Google calendar.
export async function pushMyGCal(accessToken: string): Promise<{ success: boolean; pushed: number; failed: number; total: number }> {
  const res = await fetchWithRetry(`${BASE}/gcal/my/push`, { method: 'POST', headers: authHeaders(accessToken) });
  return handleResponse(res);
}

// The caller's own private calendar events for a date range.
export async function fetchMyCalendarEvents(accessToken: string, from: string, to: string): Promise<CalendarEvent[]> {
  const res = await fetchWithRetry(`${BASE}/calendar/my/events?from=${from}&to=${to}`, { headers: authHeaders(accessToken) });
  const data = await handleResponse<{ events: CalendarEvent[] }>(res);
  return data.events || [];
}

// Anonymized free/busy for the given members over a date range. Each block
// arrives already tagged attendees:[memberId] so autoSchedule.ts's packer and
// summarizeBusy consume it unchanged. Never includes titles/notes.
export async function fetchFreeBusy(
  accessToken: string,
  memberIds: string[],
  from: string,
  to: string,
): Promise<Record<string, BusyEvent[]>> {
  if (memberIds.length === 0) return {};
  const q = `members=${encodeURIComponent(memberIds.join(','))}&from=${from}&to=${to}`;
  const res = await fetchWithRetry(`${BASE}/calendar/free-busy?${q}`, { headers: authHeaders(accessToken) });
  const data = await handleResponse<{ busy: Record<string, BusyEvent[]> }>(res);
  return data.busy || {};
}

// ---- Shared calendars (Phase 3) ----

export interface SharedCalendar {
  id: string;
  name: string;
  color?: string;
  visibility: 'members' | 'busy';
  ownerTeamMemberId: string;
  isOwner: boolean;
  myRole: 'owner' | 'editor' | 'viewer';
  subscribers: Array<{ teamMemberId: string; role: 'owner' | 'editor' | 'viewer' }>;
}

export async function fetchSharedCalendars(accessToken: string): Promise<SharedCalendar[]> {
  const res = await fetchWithRetry(`${BASE}/calendar/shared`, { headers: authHeaders(accessToken) });
  const data = await handleResponse<{ calendars: SharedCalendar[] }>(res);
  return data.calendars || [];
}

export async function createSharedCalendar(
  accessToken: string,
  input: { name: string; color?: string; visibility?: 'members' | 'busy' },
): Promise<{ id: string }> {
  const res = await fetchWithRetry(`${BASE}/calendar/shared`, { method: 'POST', headers: authHeaders(accessToken), body: JSON.stringify(input) });
  return handleResponse(res);
}

export async function updateSharedCalendar(
  accessToken: string,
  id: string,
  patch: { name?: string; color?: string; visibility?: 'members' | 'busy' },
): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/calendar/shared/${id}`, { method: 'PUT', headers: authHeaders(accessToken), body: JSON.stringify(patch) });
  return handleResponse(res);
}

export async function deleteSharedCalendar(accessToken: string, id: string): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/calendar/shared/${id}`, { method: 'DELETE', headers: authHeaders(accessToken) });
  return handleResponse(res);
}

export async function addCalendarSubscriber(
  accessToken: string, id: string, teamMemberId: string, role: 'editor' | 'viewer' = 'viewer',
): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/calendar/shared/${id}/subscribers`, { method: 'POST', headers: authHeaders(accessToken), body: JSON.stringify({ teamMemberId, role }) });
  return handleResponse(res);
}

export async function removeCalendarSubscriber(accessToken: string, id: string, memberId: string): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/calendar/shared/${id}/subscribers/${memberId}`, { method: 'DELETE', headers: authHeaders(accessToken) });
  return handleResponse(res);
}

// Create a manual event on the caller's personal calendar or a shared calendar
// they own/can edit.
export async function createCalendarEvent(
  accessToken: string,
  input: { title: string; date: string; endDate?: string; startTime?: string; endTime?: string; notes?: string; calendarId?: string },
): Promise<{ id: string; calendarId: string }> {
  const res = await fetchWithRetry(`${BASE}/calendar/events`, { method: 'POST', headers: authHeaders(accessToken), body: JSON.stringify(input) });
  return handleResponse(res);
}

export async function deleteCalendarEvent(accessToken: string, id: string): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/calendar/events/${id}`, { method: 'DELETE', headers: authHeaders(accessToken) });
  return handleResponse(res);
}

// Disconnect ALL Google Calendar connections (legacy endpoint)
export async function disconnectGCal(): Promise<{ success: boolean; disconnected: number }> {
  const res = await fetchWithRetry(`${BASE}/gcal/disconnect`, {
    method: 'POST',
    headers: headers(),
  });
  return handleResponse(res);
}

// List available Google Calendars (legacy endpoint - uses first connection)
export async function getGCalCalendars(): Promise<{ calendars: GCalCalendar[] }> {
  const res = await fetchWithRetry(`${BASE}/gcal/calendars`, { headers: headers() });
  return handleResponse(res);
}

// Save sync configuration
export async function updateGCalConfig(config: {
  calendarId?: string;
  calendarName?: string;
  syncDirection?: 'pull' | 'push' | 'both';
}): Promise<any> {
  const res = await fetchWithRetry(`${BASE}/gcal/config`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(config),
  });
  return handleResponse(res);
}

// Pull events from Google Calendar into StageFlow
export async function pullGCalEvents(): Promise<GCalSyncResult> {
  const res = await fetchWithRetry(`${BASE}/gcal/pull`, {
    method: 'POST',
    headers: headers(),
  });
  return handleResponse(res);
}

// Push StageFlow events to Google Calendar
export async function pushGCalEvents(): Promise<GCalSyncResult> {
  const res = await fetchWithRetry(`${BASE}/gcal/push`, {
    method: 'POST',
    headers: headers(),
  });
  return handleResponse(res);
}

// Two-way sync
export async function syncGCal(): Promise<{ success: boolean; pull: GCalSyncResult; push: GCalSyncResult; syncedAt: string }> {
  const res = await fetchWithRetry(`${BASE}/gcal/sync`, {
    method: 'POST',
    headers: headers(),
  });
  return handleResponse(res);
}

// Clear all imported Google Calendar events (delete them from StageFlow)
export async function clearGCalImported(calendarId?: string): Promise<{ success: boolean; removed: number; kept: number }> {
  const res = await fetchWithRetry(`${BASE}/gcal/clear-imported`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(calendarId ? { calendarId } : {}),
  });
  return handleResponse(res);
}

// Clear imported events by calendar IDs (multi-connection support)
export async function clearImportedEvents(calendarIds: string[]): Promise<{ success: boolean; removed: number; kept: number }> {
  const res = await fetchWithRetry(`${BASE}/gcal/clear-imported`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ calendarIds }),
  });
  return handleResponse(res);
}

// Unlink Google Calendar events (keep them as local-only, remove sync metadata)
export async function unlinkGCalEvents(): Promise<{ success: boolean; unlinked: number }> {
  const res = await fetchWithRetry(`${BASE}/gcal/unlink`, {
    method: 'POST',
    headers: headers(),
  });
  return handleResponse(res);
}

// ============================================
// SLACK API
// ============================================

export interface SlackConfig {
  enabled: boolean;
  isConnected: boolean;
  channelName: string;
  events: {
    statusChange: boolean;
    newDeliverable: boolean;
    highUrgency: boolean;
    blocked: boolean;
    mentions: boolean;
    communitySubmission: boolean;
  };
}

export async function getSlackConfig(): Promise<SlackConfig> {
  const res = await fetchWithRetry(`${BASE}/slack/config`, { headers: headers() });
  return handleResponse(res);
}

export async function updateSlackConfig(config: Partial<SlackConfig>): Promise<SlackConfig> {
  const res = await fetchWithRetry(`${BASE}/slack/config`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(config),
  });
  return handleResponse(res);
}

export async function sendSlackTest(): Promise<{ sent: boolean }> {
  const res = await fetchWithRetry(`${BASE}/slack/test`, {
    method: 'POST',
    headers: headers(),
  });
  return handleResponse(res);
}

// ============================================
// EMAIL (Postmark) API
// ============================================

export interface EmailConfig {
  enabled: boolean;
  isConnected: boolean;
  fromEmail: string;
  fromName: string;
  replyTo: string;
}

export async function getEmailConfig(): Promise<EmailConfig> {
  const res = await fetchWithRetry(`${BASE}/email/config`, { headers: headers() });
  return handleResponse(res);
}

export async function updateEmailConfig(config: Partial<EmailConfig>): Promise<EmailConfig> {
  const res = await fetchWithRetry(`${BASE}/email/config`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(config),
  });
  return handleResponse(res);
}

export async function sendTestEmail(to: string): Promise<{ sent: boolean; error?: string }> {
  const res = await fetchWithRetry(`${BASE}/email/test`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ to }),
  });
  return handleResponse(res);
}

// ============================================
// SOCIAL MEDIA PLATFORM CONFIG API
// ============================================

export interface SocialPlatformConfig {
  platformId: string;
  isConnected: boolean;
  enabled: boolean;
  autoPost: boolean;
  defaultHashtags: string;
  accountId: string;
  accountName: string;
  postOnApproved: boolean;
  includeProjectHashtag: boolean;
  captionTemplate: string;
  schedulingEnabled: boolean;
  defaultPostTime: string;
  updatedAt?: string;
}

export async function getSocialConfig(platformId: string): Promise<SocialPlatformConfig> {
  const res = await fetchWithRetry(`${BASE}/social/${platformId}/config`, { headers: headers() });
  return handleResponse(res);
}

export async function updateSocialConfig(platformId: string, config: Partial<SocialPlatformConfig>): Promise<SocialPlatformConfig> {
  const res = await fetchWithRetry(`${BASE}/social/${platformId}/config`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(config),
  });
  return handleResponse(res);
}

export async function sendSocialTestPost(platformId: string, message?: string): Promise<{ success: boolean; message: string }> {
  const res = await fetchWithRetry(`${BASE}/social/${platformId}/test-post`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ message }),
  });
  return handleResponse(res);
}

// ============================================
// DAILY DIGEST API
// ============================================

export interface DigestConfig {
  enabled: boolean;
  sendHour: number;
  timezone: string;
  daysOfWeek: number[];
  recipientMode: 'admins' | 'all' | 'custom';
  customRecipients: string[];
  includeOverdue: boolean;
  includeToday: boolean;
  includeUpcoming: boolean;
  includeEvents: boolean;
  includeAgenda: boolean;
  topTasksCount: number;
  lastSentAt: string | null;
  isPostmarkConnected: boolean;
  fromEmail: string;
}

export interface DigestPreview {
  generatedAt: string;
  stats: {
    totalActive: number;
    overdue: number;
    dueToday: number;
    inReview: number;
  };
  topTasks: Array<{
    id: string;
    name: string;
    projectName: string;
    dueDate: string;
    status: string;
    priority: number;
  }>;
  upcomingEvents: Array<{
    id: string;
    title: string;
    startDate: string;
    type: string;
  }>;
  /** Open agenda items grouped by the meeting they're slotted into today. */
  agenda: Array<{
    eventTitle: string;
    startTime: string | null;
    items: Array<{
      text: string;
      withPersonName: string | null;
      carriedOver: number;
    }>;
  }>;
  /** Longest-waiting open items, regardless of meeting. */
  staleAgenda: Array<{
    text: string;
    withPersonName: string | null;
    daysWaiting: number;
  }>;
}

export interface DigestSendResult {
  sent: boolean;
  successCount: number;
  totalRecipients: number;
  results: { email: string; sent: boolean; error?: string }[];
  sentAt: string;
  taskCount: number;
}

export async function fetchDigestConfig(): Promise<DigestConfig> {
  const res = await fetchWithRetry(`${BASE}/digest/config`, { headers: headers() });
  return handleResponse(res);
}

export async function updateDigestConfig(config: Partial<DigestConfig>): Promise<DigestConfig> {
  const res = await fetchWithRetry(`${BASE}/digest/config`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(config),
  });
  return handleResponse(res);
}

export async function fetchDigestPreview(): Promise<DigestPreview> {
  const res = await fetchWithRetry(`${BASE}/digest/preview`, { headers: headers() });
  return handleResponse(res);
}

export async function sendDigest(testRecipient?: string): Promise<DigestSendResult> {
  const res = await fetchWithRetry(`${BASE}/digest/send`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(testRecipient ? { testRecipient } : {}),
  });
  return handleResponse(res);
}

// ============================================
// CANVA API
// ============================================
// Every function here takes the caller's Supabase access token, like the
// /api/checkin/* and /api/hr/* families and unlike the rest of this file. The
// Canva routes reject the anon key outright: they spend a connected Canva
// grant, and the anon key ships in this bundle, so anyone holding it could
// otherwise read every design in the organization's account.
//
// Call sites destructure `accessToken` from useAuth() and early-return when it
// is null.

export interface CanvaDesign {
  id: string;
  title: string;
  thumbnail?: {
    url: string;
    width: number;
    height: number;
  };
  urls: {
    edit_url: string;
    view_url: string;
  };
  created_at: number;
  updated_at: number;
}

export interface CanvaDesignsResponse {
  items: CanvaDesign[];
  continuation?: string;
}

// The stored shape lives in mockData.ts alongside Deliverable, which owns it.
import type { CanvaDesignRef } from './mockData';
export type { CanvaDesignRef };

export interface CanvaThumbnail {
  designId: string;
  title: string;
  url: string | null;
  width?: number;
  height?: number;
  editUrl?: string;
  viewUrl?: string;
  cachedAt: number;
}

export interface CanvaStatus {
  connected: boolean;
  configured: boolean;
  displayName?: string;
  connectedAt?: string;
  connectedBy?: string;
  scopes?: string[];
  needsReconnect?: boolean;
  reason?: string;
  scopes_requested?: string[];
}

/**
 * True for a Canva URL that has almost certainly expired.
 *
 * Records created before design links stopped storing thumbnails still hold
 * one of these. Treating them as absent means the UI falls back to resolving
 * the design ID, and the next save clears the dead value — so the data heals
 * itself without a migration.
 *
 * Mirrors isExpiringCanvaAssetUrl in supabase/functions/server/canva-requests.ts,
 * which is the tested copy; there is no module shared across that boundary.
 */
export function isExpiredCanvaUrl(url?: string | null): boolean {
  if (!url) return false;
  return /(^|\/\/)([a-z0-9-]+\.)?(document-export|export-download|template)\.canva\.com/i.test(url);
}

/** Reduce a picked design to what is safe to persist. */
export function toCanvaDesignRef(design: CanvaDesign): CanvaDesignRef {
  return { id: design.id, title: design.title, editUrl: design.urls?.edit_url };
}

export async function fetchCanvaStatus(token: string): Promise<CanvaStatus> {
  const res = await fetchWithRetry(`${BASE}/canva/status`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/** Start the OAuth flow — returns the URL to send the admin to. */
export async function fetchCanvaAuthUrl(token: string): Promise<{ authUrl: string }> {
  const res = await fetchWithRetry(`${BASE}/canva/auth-url`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export async function disconnectCanva(token: string): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/canva/disconnect`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// List the user's Canva designs, with optional search and paging
export async function fetchCanvaDesigns(
  token: string,
  query?: string,
  continuation?: string,
  opts: { limit?: number; sortBy?: string } = {},
): Promise<CanvaDesignsResponse> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  if (continuation) params.set('continuation', continuation);
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.sortBy) params.set('sortBy', opts.sortBy);
  const qs = params.toString();
  const res = await fetchWithRetry(`${BASE}/canva/designs${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function fetchCanvaDesign(token: string, designId: string): Promise<CanvaDesign> {
  const res = await fetchWithRetry(`${BASE}/canva/designs/${designId}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// Connected Canva identity
export async function fetchCanvaUser(token: string): Promise<{ displayName: string; email: string }> {
  const res = await fetchWithRetry(`${BASE}/canva/user`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/**
 * Resolve current thumbnail URLs for stored design references.
 *
 * This is the read half of not storing thumbnail URLs. The server caches
 * inside Canva's 15-minute expiry window, so calling it per page render is
 * cheap and always returns a live URL.
 */
export async function fetchCanvaThumbnails(
  token: string,
  designIds: string[],
): Promise<Record<string, CanvaThumbnail | null>> {
  if (designIds.length === 0) return {};
  const res = await fetchWithRetry(`${BASE}/canva/thumbnails`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ designIds }),
  });
  const data = await handleResponse<{ thumbnails: Record<string, CanvaThumbnail | null> }>(res);
  return data.thumbnails ?? {};
}

export interface CanvaExportResult {
  jobId: string;
  status: 'success' | 'failed' | 'in_progress';
  urls?: string[];
  error?: string;
}

/**
 * Start an export job.
 *
 * Pass `wait` to have the server poll to completion and return the download
 * URLs. Those URLs expire after 24 hours — download promptly, never store them.
 */
export async function exportCanvaDesign(
  token: string,
  designId: string,
  format: 'png' | 'jpg' | 'pdf' | 'pptx' | 'gif' | 'mp4' | 'csv' | 'html_bundle' | 'html_standalone' = 'png',
  opts: { quality?: number; width?: number; height?: number; pages?: number[]; wait?: boolean } = {},
): Promise<CanvaExportResult> {
  const res = await fetchWithRetry(`${BASE}/canva/exports`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ designId, format, ...opts }),
  });
  return handleResponse(res);
}

// ============================================
// CANVA BRAND TEMPLATES & AUTOFILL
// ============================================
// Turns a StageFlow record into a finished Canva design: a brand template
// declares named data fields, a saved mapping says which record field fills
// each one, and autofill renders it. Same token rule as the rest of the Canva
// surface — the caller's access token, never the anon key.

export interface CanvaBrandTemplate {
  id: string;
  title: string;
  thumbnail?: { url: string; width: number; height: number };
  urls?: { view_url: string; edit_url: string };
  updated_at?: number;
}

export interface CanvaDataField {
  name: string;
  type: 'text' | 'image';
}

export interface CanvaFieldMappingEntry {
  source: string;
  dateStyle?: 'long' | 'medium' | 'short' | 'iso';
  separator?: string;
  prefix?: string;
  suffix?: string;
}

export type CanvaTemplateMapping = Record<string, CanvaFieldMappingEntry>;

export interface CanvaSourceField {
  key: string;
  label: string;
  kind: 'text' | 'date' | 'time' | 'list' | 'number' | 'money' | 'image';
}

export type CanvaAutofillEntityType = 'social-posts' | 'events' | 'merch' | 'projects';

export interface CanvaTemplateDataset {
  templateId: string;
  fields: CanvaDataField[];
  mapping: CanvaTemplateMapping;
  entityType: string | null;
  /** True when `mapping` is a name-derived guess rather than a saved one. */
  suggested: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface CanvaAutofillResult {
  jobId: string;
  status: 'success' | 'failed' | 'in_progress';
  design?: { id: string; title?: string; url?: string; editUrl?: string; thumbnailUrl?: string };
  error?: string;
  /** Fields left as the template drew them, with the reason. */
  skipped?: { field: string; reason: string }[];
  failedImageUploads?: string[];
}

export async function fetchCanvaBrandTemplates(
  token: string,
  query?: string,
  continuation?: string,
): Promise<{ items: CanvaBrandTemplate[]; continuation?: string }> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  if (continuation) params.set('continuation', continuation);
  const qs = params.toString();
  const res = await fetchWithRetry(`${BASE}/canva/brand-templates${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

/** The record fields each entity type can offer a design. */
export async function fetchCanvaAutofillSources(
  token: string,
): Promise<{ sources: Record<CanvaAutofillEntityType, CanvaSourceField[]> }> {
  const res = await fetchWithRetry(`${BASE}/canva/autofill/sources`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/**
 * A template's fields plus its mapping.
 *
 * Pass `entityType` so an unmapped template comes back with a name-derived
 * suggestion instead of an empty mapping — the dialog then opens pre-filled.
 */
export async function fetchCanvaTemplateDataset(
  token: string,
  templateId: string,
  entityType?: string,
): Promise<CanvaTemplateDataset> {
  const qs = entityType ? `?entityType=${encodeURIComponent(entityType)}` : '';
  const res = await fetchWithRetry(`${BASE}/canva/brand-templates/${templateId}/dataset${qs}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function saveCanvaTemplateMapping(
  token: string,
  templateId: string,
  entityType: string,
  mapping: CanvaTemplateMapping,
): Promise<{ templateId: string; updatedAt: string }> {
  const res = await fetchWithRetry(`${BASE}/canva/brand-templates/${templateId}/mapping`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ entityType, mapping }),
  });
  return handleResponse(res);
}

export async function clearCanvaTemplateMapping(token: string, templateId: string): Promise<void> {
  const res = await fetchWithRetry(`${BASE}/canva/brand-templates/${templateId}/mapping`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  await handleResponse(res);
}

/**
 * Generate a design from a record.
 *
 * Waits for the job by default and returns the finished design. A mapping
 * problem comes back as a 400 whose message names the offending fields, rather
 * than the single opaque error Canva would return.
 */
export async function generateCanvaDesign(
  token: string,
  opts: {
    templateId: string;
    entityType: CanvaAutofillEntityType;
    entityId: string;
    title?: string;
    mapping?: CanvaTemplateMapping;
    wait?: boolean;
  },
): Promise<CanvaAutofillResult> {
  const res = await fetchWithRetry(`${BASE}/canva/autofill`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(opts),
  });
  return handleResponse(res);
}

// ============================================
// CANVA EXPORT → FILE ATTACHMENT
// ============================================
// Exporting a design is only half the job: Canva's download URLs expire after
// 24 hours, so the server fetches the bytes and files them as a normal
// attachment. These return the same records the files list already renders.

export interface CanvaExportPreset {
  id: string;
  label: string;
  format: string;
  description: string;
  quality?: number;
}

export interface CanvaExportAttachment {
  id: string;
  targetType: string;
  targetId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  storagePath: string;
  label: string;
  uploadedBy: string;
  uploadedAt: string;
  source: string;
  canvaDesignId: string;
  canvaDesignTitle: string;
  canvaExportFormat: string;
}

export interface CanvaExportToFileResult {
  ok: boolean;
  status: 'success' | 'failed' | 'in_progress';
  attachments: CanvaExportAttachment[];
  error?: string;
  /** Pages Canva rendered that could not be stored. */
  failedPages?: number[];
}

export async function fetchCanvaExportPresets(
  token: string,
): Promise<{ presets: CanvaExportPreset[]; formats: string[] }> {
  const res = await fetchWithRetry(`${BASE}/canva/export-presets`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/**
 * Export a design and file the result against a record.
 *
 * Pass either a `preset` (an intent like "print") or an explicit `format`.
 * The call blocks while Canva renders — expect a few seconds.
 */
export async function exportCanvaDesignToFile(
  token: string,
  opts: {
    designId: string;
    designTitle?: string;
    targetType: 'deliverable' | 'content' | 'merch';
    targetId: string;
    preset?: string;
    format?: string;
    quality?: number;
    width?: number;
    height?: number;
    pages?: number[];
    label?: string;
  },
): Promise<CanvaExportToFileResult> {
  const res = await fetchWithRetry(`${BASE}/canva/exports/attach`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(opts),
  });
  return handleResponse(res);
}

export interface CanvaExportRecord {
  designId: string;
  format: string;
  exportedAt: string;
  fileIds: string[];
}

/** What has already been exported for a record, so the UI can say so. */
export async function fetchCanvaExportHistory(
  token: string,
  targetType: string,
  targetId: string,
): Promise<{ exports: CanvaExportRecord[] }> {
  const res = await fetchWithRetry(
    `${BASE}/canva/exports/history/${targetType}/${targetId}`,
    { headers: authHeaders(token) },
  );
  return handleResponse(res);
}

// ============================================
// AI COPY GENERATION
// ============================================

export async function generateAICopy(data: {
  name: string;
  existingCopy: string;
  hashtags: string;
  category: string;
  platforms: string[];
  type: string;
  notes: string;
}): Promise<{ copies: Record<string, { copy: string; charCount: number; limit: number; overLimit: boolean }> }> {
  const res = await fetchWithRetry(`${BASE}/ai/generate-copy`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

// ============================================
// AI DELIVERABLE AUTOFILL
// ============================================

export interface FieldProposal<T> {
  value: T;
  rationale?: string;
  confidence?: 'high' | 'medium' | 'low';
}
export interface WorkingTeamProposal {
  values: string[];
  rationale?: string;
  confidence?: 'high' | 'medium' | 'low';
}

export interface AutofillProposals {
  department?: FieldProposal<string>;
  type?: FieldProposal<string>;
  energyLevel?: FieldProposal<'quick-win' | 'medium-effort' | 'deep-focus'>;
  estimatedMinutes?: FieldProposal<number>;
  kickoffDate?: FieldProposal<string>;
  dueDate?: FieldProposal<string>;
  workingTeam?: WorkingTeamProposal;
  reviewBy?: FieldProposal<string>;
  isClientWork?: FieldProposal<boolean>;
  isVolunteerEligible?: FieldProposal<boolean>;
  volunteerDescription?: FieldProposal<string>;
  volunteerSlotsNeeded?: FieldProposal<number>;
  deptData?: Record<string, FieldProposal<string | string[]>>;
}

export interface AutofillRequest {
  name: string;
  task: string;
  department?: string;
  projectContext?: {
    projectId: string;
    projectName: string;
    projectDescription?: string;
    projectKickoffDate?: string;
    projectDueDate?: string | null;
    projectStatus?: string;
    projectLead?: string;
  } | null;
  currentValues: Record<string, unknown>;
  teamRoster: Array<{ name: string; title?: string; departments?: string[] }>;
  deptOptions: DeptFormOptions | null;
  today: string;
}

export interface AutofillResponse {
  proposals: AutofillProposals;
  checklistItems: Array<{ text: string; rationale?: string }>;
  model: string;
}

export async function autofillDeliverable(payload: AutofillRequest): Promise<AutofillResponse> {
  const res = await fetchWithRetry(`${BASE}/ai/deliverable-autofill`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// ============================================
// AI PLAN DELIVERABLE (Plan with AI)
// ============================================

import type { FeasibilityLevel } from './mockData';

export interface PlanStepProposal {
  text: string;
  estimatedMinutes: number;
  rationale?: string;
}

export interface AssigneeSuggestion {
  name: string;
  rationale?: string;
  confidence?: 'high' | 'medium' | 'low';
}

export interface FeasibilityAssessment {
  level: FeasibilityLevel;
  rationale?: string;
  concerns?: string[];
}

export interface PlanDeliverableRequest {
  requestText: string;
  today: string; // todayLocalStr() — never toISOString()
  projects: Array<{
    id: string;
    name: string;
    description?: string;
    kickoffDate?: string;
    dueDate?: string | null;
    status?: string;
  }>;
  // busySummary comes from summarizeBusy() in autoSchedule.ts — the model
  // weighs availability without ever seeing the raw calendar.
  teamRoster: Array<{ name: string; title?: string; departments?: string[]; busySummary?: string }>;
}

export interface PlanDeliverableProposals {
  name: FieldProposal<string>;
  projectId?: FieldProposal<string>;
  department?: FieldProposal<string>;
  energyLevel?: FieldProposal<'quick-win' | 'medium-effort' | 'deep-focus'>;
  task?: FieldProposal<string>;
  kickoffDate?: FieldProposal<string>;
  dueDate?: FieldProposal<string>;
}

export interface PlanDeliverableResponse {
  proposals: PlanDeliverableProposals;
  steps: PlanStepProposal[];
  suggestedAssignee?: AssigneeSuggestion;
  feasibility?: FeasibilityAssessment;
  model: string;
}

export async function planDeliverable(payload: PlanDeliverableRequest): Promise<PlanDeliverableResponse> {
  const res = await fetchWithRetry(`${BASE}/ai/plan-deliverable`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// ============================================
// AI UPDATE DELIVERABLE (revise an existing deliverable)
// ============================================

export interface UpdateStepProposal {
  existingId: string | null; // an existing OPEN checklist item id, or null for a new step
  text: string;
  estimatedMinutes: number;
  rationale?: string;
}

export interface UpdateDeliverableRequest {
  requestText: string;
  today: string;
  deliverable: { name: string; task?: string; kickoffDate?: string; dueDate?: string };
  openSteps: Array<{ id: string; text: string; estimatedMinutes?: number }>;
  completedSteps: Array<{ id: string; text: string }>;
}

export interface UpdateDeliverableProposals {
  kickoffDate?: FieldProposal<string>;
  dueDate?: FieldProposal<string>;
}

export interface UpdateDeliverableResponse {
  proposals: UpdateDeliverableProposals;
  steps: UpdateStepProposal[];
  removedStepIds: string[]; // open items the AI dropped — shown as removals to confirm
  model: string;
}

export async function updateDeliverableAI(payload: UpdateDeliverableRequest): Promise<UpdateDeliverableResponse> {
  const res = await fetchWithRetry(`${BASE}/ai/update-deliverable`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// ============================================
// CONTENT SCHEDULING
// ============================================

export async function scheduleContentPost(
  postId: string,
  scheduledTime: string,
  platforms: string[],
): Promise<{ success: boolean; message: string; disconnectedPlatforms?: string[] }> {
  const res = await fetchWithRetry(`${BASE}/content/schedule`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ postId, scheduledTime, platforms }),
  });
  return handleResponse(res);
}

export async function cancelScheduledPost(postId: string): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/content/schedule/${postId}`, {
    method: 'DELETE',
    headers: headers(),
  });
  return handleResponse(res);
}

// ============================================
// TEAM MEMBER TOOLS API
// ============================================

export async function uploadHeadshot(memberId: string, file: File): Promise<{ signedUrl: string; storagePath: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('memberId', memberId);
  const res = await fetchWithRetry(`${BASE}/team/headshot`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${publicAnonKey}` },
    body: formData,
  });
  return handleResponse(res);
}

export async function deleteHeadshot(memberId: string): Promise<void> {
  const res = await fetchWithRetry(`${BASE}/team/headshot/${memberId}`, {
    method: 'DELETE',
    headers: headers(),
  });
  return handleResponse(res);
}

export async function refreshHeadshots(): Promise<{ refreshed: number }> {
  const res = await fetchWithRetry(`${BASE}/team/refresh-headshots`, {
    method: 'POST',
    headers: headers(),
  });
  return handleResponse(res);
}

export async function resetPassword(memberId: string, newPassword: string, adminToken?: string): Promise<{ success: boolean; message: string }> {
  const res = await fetchWithRetry(`${BASE}/team/reset-password`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ memberId, newPassword, adminToken }),
  });
  return handleResponse(res);
}

export async function updateTeamEmail(memberId: string, newEmail: string, adminToken?: string): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/team/update-email`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ memberId, newEmail, adminToken }),
  });
  return handleResponse(res);
}

export async function deleteTeamMember(memberId: string): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/team/${memberId}`, {
    method: 'DELETE',
    headers: headers(),
  });
  return handleResponse(res);
}

// Fetch posting queue info (derived from social posts with scheduled/posted/failed statuses)
export async function fetchPostingQueue(): Promise<{ total: number; queue: any[] }> {
  try {
    const posts = await fetchCollection<any>('social-posts');
    const queuePosts = posts.filter((p: any) =>
      p.autoPostStatus && ['scheduled', 'posted', 'failed'].includes(p.autoPostStatus)
    );
    return {
      total: queuePosts.length,
      queue: queuePosts.map((p: any) => ({
        postId: p.id,
        status: p.autoPostStatus || 'scheduled',
        scheduledTime: p.scheduledDate || p.date || new Date().toISOString(),
        platform: p.platform,
      })),
    };
  } catch {
    return { total: 0, queue: [] };
  }
}

// Process posting queue — called by TV Dashboard every 60s
export async function processPostingQueue(): Promise<{
  processed: number;
  queueDepth: number;
  nextDue: string | null;
  processedAt?: string;
  results?: any[];
}> {
  try {
    const res = await fetchWithRetry(`${BASE}/content/process-queue`, {
      method: 'POST',
      headers: headers(),
    });
    return handleResponse(res);
  } catch {
    return { processed: 0, queueDepth: 0, nextDue: null };
  }
}

// ============================================
// CONTENT IMAGE API
// ============================================

export interface ContentImageResult {
  imageUrl: string | null;
  imageStoragePath: string;
  imageFileName: string;
  imageSource: 'upload' | 'canva' | 'url';
}

// Upload a post image
export async function uploadContentImage(file: File, postId?: string): Promise<ContentImageResult> {
  const formData = new FormData();
  formData.append('file', file);
  if (postId) formData.append('postId', postId);

  const res = await fetchWithRetry(`${BASE}/content/upload-image`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${publicAnonKey}` },
    body: formData,
  });
  return handleResponse(res);
}

// Delete a post image from storage
export async function deleteContentImage(storagePath: string): Promise<void> {
  const res = await fetchWithRetry(`${BASE}/content/delete-image`, {
    method: 'DELETE',
    headers: headers(),
    body: JSON.stringify({ storagePath }),
  });
  await handleResponse(res);
}

// Refresh an expired signed URL for a content image
export async function refreshContentImageUrl(storagePath: string): Promise<{ imageUrl: string | null }> {
  const res = await fetchWithRetry(`${BASE}/content/refresh-image-url`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ storagePath }),
  });
  return handleResponse(res);
}

// ============================================
// DEPARTMENT FORM OPTIONS CONFIG
// ============================================

export interface DeptFormOptions {
  rooms: string[];
  costumeRooms: string[];
  propRooms: string[];
  techTypes: string[];
  mediaTypes: string[];
  mediaFormats: string[];
  costumeTypes: string[];
  costumePieces: string[];
  propTypes: string[];
  websiteTypes: string[];
  websiteSections: string[];
  marketingTypes: string[];
  graphicTypes: string[];
  apparelMerchTypes: string[];
  printTypes: string[];
  facilityTypes: string[];
  facilityTasks: string[];
  measurementStatuses: string[];
}

export async function fetchDeptFormOptions(): Promise<DeptFormOptions | null> {
  return fetchItemOptional<DeptFormOptions>('dept-form-config', 'options');
}

export async function saveDeptFormOptions(opts: DeptFormOptions): Promise<DeptFormOptions> {
  const res = await fetchWithRetry(`${BASE}/dept-form-config/options`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(opts),
  });
  return handleResponse(res);
}

// ============================================
// STAGESM INTEGRATION
// ============================================

// One StageCommand production, with the credentials that reach it. Keys
// are per-production over there, so connecting a second show means a
// second entry — never a replacement. The api_key itself never leaves the
// server: `has_api_key` says whether one is stored, `api_key_masked`
// shows its tail so the operator can tell two keys apart.
export interface StageCommandConnection {
  id: string;
  label: string;
  base_url: string;
  production_id: string;
  production_title: string;
  project_id: string;
  enabled: boolean;
  has_api_key: boolean;
  api_key_masked: string;
  last_sync_at?: string;
  last_sync_error?: string | null;
}

export interface StageSMConfig {
  enabled: boolean;
  // Every connected production. The list is the source of truth for both
  // directions: inbound pushes are routed by production_id, and a pull
  // sync walks it.
  connections: StageCommandConnection[];
  // Mirrors of the first credentialed connection, kept so older callers
  // (and StageCommand builds still reading the flat shape) keep working.
  // Prefer `connections`.
  stagesm_base_url: string;
  stagesm_api_key: string;
  linked_production_id?: string;
  linked_production_title?: string;
  production_project_mappings: Array<{
    production_id: string;
    production_name: string;
    project_id: string;
  }>;
  auto_pull: boolean;
  department_mapping?: Record<string, string>;
  last_sync_at?: string;
  last_sync_error?: string | null;
}

export interface StageCommandSyncResult {
  success: boolean;
  pulled: number;
  pushed: number;
  created?: number;
  updated?: number;
  errors: string[];
  taskCount?: number;
  // One entry per production the sync touched.
  results?: Array<{
    connection_id: string;
    production_id?: string;
    production_title?: string;
    created?: number;
    updated?: number;
    taskCount?: number;
    errors?: string[];
    error?: string;
  }>;
}

export interface StageSMDepartment {
  id: string;
  label: string;
  default_stageflow_department: string | null;
}

export async function getStageSMConfig(): Promise<{ config: StageSMConfig }> {
  const res = await fetchWithRetry(`${BASE}/stagesm/config`, { headers: headers() });
  return handleResponse(res);
}

export async function updateStageSMConfig(config: Partial<StageSMConfig>): Promise<{ config: StageSMConfig }> {
  const res = await fetchWithRetry(`${BASE}/stagesm/config`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(config),
  });
  return handleResponse(res);
}

export async function testStageSMConnection(
  creds?: { stagesm_base_url?: string; stagesm_api_key?: string },
): Promise<{
  success: boolean;
  message: string;
  production_id?: string;
  production_title?: string;
}> {
  const res = await fetchWithRetry(`${BASE}/stagesm/test-connection`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(creds || {}),
  });
  return handleResponse(res);
}

// Add a production, or edit one (pass its `id`). The server pings
// StageCommand to resolve which production the key belongs to before it
// saves — that id is what routes inbound tasks. Omit `api_key` to keep
// the stored one.
export async function saveStageSMConnection(connection: {
  id?: string;
  label?: string;
  base_url?: string;
  api_key?: string;
  project_id?: string;
  enabled?: boolean;
}): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  connection_id?: string;
  production_id?: string;
  production_title?: string;
  config: StageSMConfig;
}> {
  const res = await fetchWithRetry(`${BASE}/stagesm/connections`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(connection),
  });
  return handleResponse(res);
}

export async function testStageSMConnectionById(connectionId: string): Promise<{
  success: boolean;
  message: string;
  production_id?: string;
  production_title?: string;
  config?: StageSMConfig;
}> {
  const res = await fetchWithRetry(`${BASE}/stagesm/connections/${encodeURIComponent(connectionId)}/test`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({}),
  });
  return handleResponse(res);
}

// Deliverables already imported from the production stay in StageFlow —
// only the link is removed.
export async function deleteStageSMConnection(connectionId: string): Promise<{ success: boolean; message: string; config: StageSMConfig }> {
  const res = await fetchWithRetry(`${BASE}/stagesm/connections/${encodeURIComponent(connectionId)}`, {
    method: 'DELETE',
    headers: headers(),
  });
  return handleResponse(res);
}

// Pull every connected production, or just one when `connectionId` is
// given. One production failing doesn't cancel the rest — check
// `results` for the per-production outcome.
export async function syncWithStageSM(connectionId?: string): Promise<StageCommandSyncResult> {
  const res = await fetchWithRetry(`${BASE}/stagesm/sync`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(connectionId ? { connection_id: connectionId } : {}),
  });
  return handleResponse(res);
}

export async function getStageSMLinkedDeliverables(): Promise<{ deliverables: Array<{ deliverable_id: string; production_id: string; task_type: string; task_id: string }> }> {
  const res = await fetchWithRetry(`${BASE}/stagesm/linked-deliverables`, { headers: headers() });
  return handleResponse(res);
}

// Server-side proxy so the Stagesm bearer token never leaves the edge
// function. Uses whichever base URL + key the caller supplies; when
// omitted, the server falls back to the saved global config.
export async function fetchStageSMDepartments(
  creds?: { stagesm_base_url?: string; stagesm_api_key?: string },
): Promise<{ departments: StageSMDepartment[] }> {
  const res = await fetchWithRetry(`${BASE}/stagesm/fetch-departments`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(creds || {}),
  });
  return handleResponse(res);
}

// ============================================
// DAILY RESET (morning verse + personalized AI briefing)
// ============================================

export interface DailyResetVerse {
  text: string;
  reference: string;
  version: string;
}

export interface DailyResetStats {
  personalActive: number;
  deptActive: number;
  overdue: number;
  dueToday: number;
  inReview: number;
}

export interface DailyReset {
  dayKey: string;              // reset-day key (YYYY-MM-DD, 4 AM cutoff)
  generatedAt: string;
  source: 'ai' | 'fallback';
  verse: DailyResetVerse;
  summary: string;             // Markdown
  stats: DailyResetStats;
  user: { name: string; departments: string[] };
}

export interface DailyResetConfig {
  enabled: boolean;
  resetHour: number;
  bibleApi: string;
  bibleVersion: string;
  updatedAt?: string;
}

// Fetch (or lazily generate + cache) the current reset-day briefing for a user.
export async function fetchDailyReset(userId: string, opts?: { force?: boolean }): Promise<DailyReset> {
  const qs = new URLSearchParams({ userId });
  if (opts?.force) qs.set('force', '1');
  const res = await fetchWithRetry(`${BASE}/daily-reset/today?${qs.toString()}`, { headers: headers() });
  return handleResponse(res);
}

export async function getDailyResetConfig(): Promise<DailyResetConfig> {
  const res = await fetchWithRetry(`${BASE}/daily-reset/config`, { headers: headers() });
  return handleResponse(res);
}

export async function updateDailyResetConfig(patch: Partial<DailyResetConfig>): Promise<DailyResetConfig> {
  const res = await fetchWithRetry(`${BASE}/daily-reset/config`, {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(patch),
  });
  return handleResponse(res);
}
// ============================================
// MARKETING SITE CMS
// ============================================
// Types live in data/marketing.ts (shared with the renderer + editor).
// Admin mutations carry X-Editor-Name so page history shows who edited
// (best-effort attribution — this server's endpoints are anon-key, the
// same access model as email config and moderation settings).

import type { MarketingPageDoc, MarketingPageRecord, MarketingPageSummary } from './marketing';

const editorHeaders = (editorName?: string) => ({
  ...headers(),
  ...(editorName ? { 'X-Editor-Name': editorName } : {}),
});

// Unauthenticated published-page content for /home and /p/:slug.
export async function getPublishedMarketingPage(slug: string): Promise<{ page: MarketingPageDoc; seeded?: boolean }> {
  const res = await fetchWithRetry(`${BASE}/public/marketing/${encodeURIComponent(slug)}`, { headers: headers() });
  return handleResponse(res);
}

export async function adminListMarketingPages(): Promise<{ pages: MarketingPageSummary[] }> {
  const res = await fetchWithRetry(`${BASE}/admin/marketing/pages`, { headers: headers() });
  return handleResponse(res);
}

export async function adminGetMarketingPage(slug: string): Promise<{ page: MarketingPageRecord; summary: MarketingPageSummary }> {
  const res = await fetchWithRetry(`${BASE}/admin/marketing/pages/${encodeURIComponent(slug)}`, { headers: headers() });
  return handleResponse(res);
}

export async function adminCreateMarketingPage(slug: string, title: string, editorName?: string): Promise<{ success: boolean; page: MarketingPageRecord; summary: MarketingPageSummary }> {
  const res = await fetchWithRetry(`${BASE}/admin/marketing/pages`, {
    method: 'POST', headers: editorHeaders(editorName), body: JSON.stringify({ slug, title }),
  });
  return handleResponse(res);
}

export async function adminSaveMarketingDraft(
  slug: string,
  doc: Pick<MarketingPageDoc, 'title' | 'description' | 'sections'>,
  editorName?: string,
): Promise<{ success: boolean; summary: MarketingPageSummary }> {
  const res = await fetchWithRetry(`${BASE}/admin/marketing/pages/${encodeURIComponent(slug)}`, {
    method: 'PUT', headers: editorHeaders(editorName), body: JSON.stringify(doc),
  });
  return handleResponse(res);
}

export async function adminPublishMarketingPage(slug: string, editorName?: string): Promise<{ success: boolean; summary: MarketingPageSummary }> {
  const res = await fetchWithRetry(`${BASE}/admin/marketing/pages/${encodeURIComponent(slug)}/publish`, {
    method: 'POST', headers: editorHeaders(editorName), body: JSON.stringify({}),
  });
  return handleResponse(res);
}

export async function adminUnpublishMarketingPage(slug: string, editorName?: string): Promise<{ success: boolean; summary: MarketingPageSummary }> {
  const res = await fetchWithRetry(`${BASE}/admin/marketing/pages/${encodeURIComponent(slug)}/unpublish`, {
    method: 'POST', headers: editorHeaders(editorName), body: JSON.stringify({}),
  });
  return handleResponse(res);
}

export async function adminDiscardMarketingDraft(slug: string, editorName?: string): Promise<{ success: boolean; page: MarketingPageDoc; summary: MarketingPageSummary }> {
  const res = await fetchWithRetry(`${BASE}/admin/marketing/pages/${encodeURIComponent(slug)}/discard-draft`, {
    method: 'POST', headers: editorHeaders(editorName), body: JSON.stringify({}),
  });
  return handleResponse(res);
}

export async function adminRestoreMarketingRevision(slug: string, revisionId: string, editorName?: string): Promise<{ success: boolean; page: MarketingPageDoc; summary: MarketingPageSummary }> {
  const res = await fetchWithRetry(`${BASE}/admin/marketing/pages/${encodeURIComponent(slug)}/restore`, {
    method: 'POST', headers: editorHeaders(editorName), body: JSON.stringify({ revision_id: revisionId }),
  });
  return handleResponse(res);
}

export async function adminDeleteMarketingPage(slug: string): Promise<{ success: boolean; reset_to_default: boolean }> {
  const res = await fetchWithRetry(`${BASE}/admin/marketing/pages/${encodeURIComponent(slug)}`, {
    method: 'DELETE', headers: headers(),
  });
  return handleResponse(res);
}

// ============================================
// HR / Employee Hub
// ============================================
// Every function here takes the caller's Supabase access token as its first
// argument and sends it via authHeaders — never the anon `headers()` the rest
// of this file uses. That is not a style choice: the /api/hr/* surface rejects
// the anon key outright (see hr-auth.tsx), because it is the one part of this
// server holding data that should not be world-readable.
//
// Call sites follow the /api/checkin/* pattern: destructure `accessToken` from
// useAuth() and early-return when it is null.

import type { HrRole } from './mockData';

export interface HrIdentity {
  teamMemberId: string;
  name: string;
  email: string;
  appRole: 'admin' | 'member';
  hrRole: HrRole;
  directReportIds: string[];
  capabilities: { isHrAdmin: boolean; isManager: boolean };
}

export interface HrRoleGrant {
  team_member_id: string;
  email: string;
  hr_role: HrRole;
  granted_by: string | null;
  granted_at: string | null;
}

export type HrEmploymentType =
  | 'w2-full-time' | 'w2-part-time' | 'w2-seasonal'
  | 'contractor-1099' | 'volunteer' | 'intern';

export type HrEmploymentStatus = 'active' | 'on-leave' | 'seasonal-inactive' | 'terminated';

/**
 * Fields arrive already redacted by viewer — a colleague gets the directory
 * subset, the employee and HR get more. Everything past the first few is
 * optional because whether it is present *is* the permission signal.
 */
export interface HrEmployeeProfile {
  team_member_id: string;
  employment_type: HrEmploymentType;
  status: HrEmploymentStatus;
  manager_team_member_id?: string | null;
  work_location_id?: string | null;
  preferred_name?: string | null;
  pronouns?: string | null;
  hire_date?: string | null;
  original_hire_date?: string | null;
  union_affiliation?: string | null;
  emergency_contacts?: Array<{ name?: string; phone?: string; relationship?: string }> | null;
  birth_date?: string | null;
  personal_email?: string | null;
  personal_phone?: string | null;
  address?: { line1?: string; line2?: string; city?: string; state?: string; postal?: string } | null;
  notes?: string | null;
  updated_at?: string;
}

export interface HrEmploymentRecord {
  id: string;
  team_member_id: string;
  start_date: string;
  end_date: string | null;
  employment_type: HrEmploymentType;
  title: string | null;
  department_id: string | null;
  project_id: string | null;
  fte: number | null;
  is_exempt: boolean | null;
  termination_type: string | null;
  termination_reason: string | null;
  is_rehire_eligible: boolean | null;
}

export interface HrTenure {
  currentStintDays: number;
  totalServiceDays: number;
  calendarSpanDays: number;
  stintCount: number;
  isRehire: boolean;
  firstHireDate: string | null;
}

export interface HrDirectoryEntry {
  teamMemberId: string;
  name: string;
  email: string;
  title: string | null;
  departments: string[];
  avatarColor: string | null;
  avatarUrl: string | null;
  isActive: boolean;
  /** null when HR has not created a record for this person yet. */
  profile: HrEmployeeProfile | null;
}

export interface HrPersonDetail extends HrDirectoryEntry {
  employmentRecords: HrEmploymentRecord[];
  tenure: HrTenure | null;
  viewerCan: { editProfile: boolean; editEmployment: boolean; viewCompensation: boolean };
}

export interface HrOrgNode {
  teamMemberId: string;
  name: string;
  reports: HrOrgNode[];
}

export interface HrOrgChart {
  roots: HrOrgNode[];
  /** Manager ids that point at nobody. Surfaced so an admin can fix the data. */
  orphanIds: string[];
  /** Members in a reporting loop; their manager edge was ignored to keep the tree finite. */
  cycleIds: string[];
  departmentHeads: Array<{ departmentId: string; name: string; headTeamMemberId: string }>;
}

export interface HrCompensationEntry {
  id: string;
  team_member_id: string;
  effective_date: string;
  pay_type: 'salary' | 'hourly' | 'per-production' | 'stipend';
  amount: number;
  currency: string;
  pay_frequency: string | null;
  reason: string | null;
  recorded_by: string | null;
}

export interface HrAuditEntry {
  id: string;
  occurred_at: string;
  actor_team_member_id: string | null;
  actor_email: string | null;
  action: 'read' | 'create' | 'update' | 'delete' | 'grant' | 'revoke';
  entity: string;
  entity_id: string | null;
  subject_team_member_id: string | null;
  changes: Array<{ field: string; before: unknown; after: unknown }> | null;
}

/** The caller's HR identity and resolved role. */
export async function fetchHrIdentity(token: string): Promise<HrIdentity> {
  const res = await fetchWithRetry(`${BASE}/hr/me`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/** Every HR role grant. HR admins only. */
export async function fetchHrRoleGrants(token: string): Promise<{ grants: HrRoleGrant[] }> {
  const res = await fetchWithRetry(`${BASE}/hr/admin/role-grants`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/**
 * Grant or revoke an HR role, and/or set someone's manager. HR admins only —
 * being a StageFlow admin is not sufficient.
 */
export async function updateHrRoleGrant(
  token: string,
  teamMemberId: string,
  patch: { hrRole?: HrRole; managerTeamMemberId?: string | null },
): Promise<{ teamMemberId: string; hrRole: HrRole; managerTeamMemberId: string | null }> {
  const res = await fetchWithRetry(`${BASE}/hr/admin/role-grants/${encodeURIComponent(teamMemberId)}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(patch),
  });
  return handleResponse(res);
}

/**
 * The HR audit trail. Scoped server-side by row-level security: an employee
 * sees their own entries, a manager their direct reports', an HR admin all.
 */
export async function fetchHrAuditLog(
  token: string,
  subjectTeamMemberId?: string,
): Promise<{ entries: HrAuditEntry[] }> {
  const qs = subjectTeamMemberId ? `?subject=${encodeURIComponent(subjectTeamMemberId)}` : '';
  const res = await fetchWithRetry(`${BASE}/hr/audit${qs}`, { headers: authHeaders(token) });
  return handleResponse(res);
}

// ─── HR people ────────────────────────────────────────────────────

/** The staff directory. Profile fields come pre-redacted for the caller. */
export async function fetchHrDirectory(
  token: string,
  opts: { includeInactive?: boolean } = {},
): Promise<{ people: HrDirectoryEntry[] }> {
  const qs = opts.includeInactive ? '?includeInactive=true' : '';
  const res = await fetchWithRetry(`${BASE}/hr/people${qs}`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/** One person: profile, employment history, tenure, and what the viewer may do. */
export async function fetchHrPerson(token: string, teamMemberId: string): Promise<HrPersonDetail> {
  const res = await fetchWithRetry(`${BASE}/hr/people/${encodeURIComponent(teamMemberId)}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

/**
 * Update a profile. HR admins may set any field; an employee may set only
 * their own contact details and pronouns — the server rejects the rest rather
 * than silently dropping it, so surface `errors` to the user.
 */
export async function updateHrProfile(
  token: string,
  teamMemberId: string,
  patch: Partial<HrEmployeeProfile>,
): Promise<{ profile: HrEmployeeProfile }> {
  const res = await fetchWithRetry(`${BASE}/hr/people/${encodeURIComponent(teamMemberId)}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(patch),
  });
  return handleResponse(res);
}

/** Add an employment stint. HR admins only. */
export async function createHrEmploymentRecord(
  token: string,
  teamMemberId: string,
  record: Partial<HrEmploymentRecord>,
): Promise<{ record: HrEmploymentRecord }> {
  const res = await fetchWithRetry(
    `${BASE}/hr/people/${encodeURIComponent(teamMemberId)}/employment`,
    { method: 'POST', headers: authHeaders(token), body: JSON.stringify(record) },
  );
  return handleResponse(res);
}

/** The reporting tree, with dangling and looping edges reported separately. */
export async function fetchHrOrgChart(token: string): Promise<HrOrgChart> {
  const res = await fetchWithRetry(`${BASE}/hr/org-chart`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/** Pay history. HR admins only, and every read is audited. */
export async function fetchHrCompensation(
  token: string,
  teamMemberId: string,
): Promise<{ history: HrCompensationEntry[] }> {
  const res = await fetchWithRetry(
    `${BASE}/hr/people/${encodeURIComponent(teamMemberId)}/compensation`,
    { headers: authHeaders(token) },
  );
  return handleResponse(res);
}

/** Record a pay change. A raise is a new row — history is never edited. */
export async function createHrCompensationEntry(
  token: string,
  teamMemberId: string,
  entry: Partial<HrCompensationEntry>,
): Promise<{ entry: HrCompensationEntry }> {
  const res = await fetchWithRetry(
    `${BASE}/hr/people/${encodeURIComponent(teamMemberId)}/compensation`,
    { method: 'POST', headers: authHeaders(token), body: JSON.stringify(entry) },
  );
  return handleResponse(res);
}

// ─── HR policies & documents ──────────────────────────────────────

export type HrPolicyStatus = 'draft' | 'published' | 'archived';

export type HrAckStatus =
  | 'not-required' | 'outstanding' | 'superseded' | 'expired' | 'current' | 'not-applicable';

export interface HrAckState {
  status: HrAckStatus;
  /** True when this person owes an action right now. */
  owed: boolean;
  acknowledgedAt: string | null;
  acknowledgedVersion: number | null;
  dueDate: string | null;
}

export interface HrPolicyVersion {
  id: string;
  document_id: string;
  version: number;
  body_markdown?: string | null;
  storage_path?: string | null;
  file_name?: string | null;
  effective_date?: string | null;
  change_summary?: string | null;
  published_at: string;
  published_by?: string | null;
}

export interface HrPolicy {
  id: string;
  slug: string;
  title: string;
  category: string | null;
  summary: string | null;
  requires_acknowledgement: boolean;
  reacknowledge_months: number | null;
  applies_to_employment_types: string[];
  applies_to_department_ids: string[];
  status: HrPolicyStatus;
  current_version_id: string | null;
  sort_order: number;
  /** Whether this policy is scoped to the caller — separate from readability. */
  assigned: boolean;
  currentVersion: HrPolicyVersion | null;
  acknowledgement: HrAckState;
}

export interface HrPolicyDetail {
  policy: HrPolicy;
  currentVersion: HrPolicyVersion | null;
  /** Full history for HR admins; just the live version for everyone else. */
  versions: HrPolicyVersion[];
  /** Short-lived signed URL for a companion PDF, if the version has one. */
  fileUrl: string | null;
  assigned: boolean;
  acknowledgement: HrAckState;
}

export interface HrComplianceMatrix {
  people: Array<{ teamMemberId: string; outstanding: string[]; current: string[]; total: number }>;
  outstandingByDocument: Record<string, number>;
  compliantCount: number;
  assignedCount: number;
  documents: Array<{ id: string; slug: string; title: string }>;
}

export interface HrEmployeeDocument {
  id: string;
  team_member_id: string;
  category: string;
  title: string | null;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  visibility: 'employee' | 'manager' | 'hr-only';
  retention_until: string | null;
  uploaded_by: string | null;
  uploaded_at: string;
}

/** The handbook library, each policy tagged with where the caller stands. */
export async function fetchHrPolicies(
  token: string,
): Promise<{ policies: HrPolicy[]; outstandingCount: number }> {
  const res = await fetchWithRetry(`${BASE}/hr/policies`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export async function fetchHrPolicy(token: string, slug: string): Promise<HrPolicyDetail> {
  const res = await fetchWithRetry(`${BASE}/hr/policies/${encodeURIComponent(slug)}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

/**
 * Acknowledge the current version. `typedName` must match the caller's name on
 * record — the server rejects a blank or mismatched box, because a record
 * created from one looks like evidence without being any.
 */
export async function acknowledgeHrPolicy(
  token: string,
  slug: string,
  typedName: string,
): Promise<{ acknowledged: boolean; version: number }> {
  const res = await fetchWithRetry(`${BASE}/hr/policies/${encodeURIComponent(slug)}/acknowledge`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ typedName }),
  });
  return handleResponse(res);
}

/** Create a policy. HR admins only. */
export async function createHrPolicy(
  token: string,
  input: Partial<HrPolicy> & { title: string },
): Promise<{ policy: HrPolicy }> {
  const res = await fetchWithRetry(`${BASE}/hr/policies`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
  });
  return handleResponse(res);
}

export async function updateHrPolicy(
  token: string,
  slug: string,
  patch: Partial<HrPolicy>,
): Promise<{ policy: HrPolicy }> {
  const res = await fetchWithRetry(`${BASE}/hr/policies/${encodeURIComponent(slug)}`, {
    method: 'PUT', headers: authHeaders(token), body: JSON.stringify(patch),
  });
  return handleResponse(res);
}

/**
 * Publish a new version.
 *
 * From v2 onward this RE-OPENS the acknowledgement for everyone in scope —
 * agreement attaches to a version, not a document. `reacknowledgementRequired`
 * comes back so the UI can say so rather than letting HR discover it.
 */
/**
 * Publish a new version of a policy.
 *
 * Takes markdown, a file, or both. Both is the useful case for a handbook: the
 * PDF is what people signed, the markdown is what makes it readable in the app
 * without downloading anything.
 */
export async function publishHrPolicyVersion(
  token: string,
  slug: string,
  version: {
    body_markdown?: string;
    change_summary?: string;
    effective_date?: string;
    file?: File | null;
  },
): Promise<{ version: HrPolicyVersion; reacknowledgementRequired: boolean }> {
  const url = `${BASE}/hr/policies/${encodeURIComponent(slug)}/versions`;

  if (version.file) {
    const form = new FormData();
    form.append('file', version.file);
    if (version.body_markdown) form.append('body_markdown', version.body_markdown);
    if (version.change_summary) form.append('change_summary', version.change_summary);
    if (version.effective_date) form.append('effective_date', version.effective_date);
    const res = await fetchWithRetry(url, {
      // No Content-Type: the browser sets the multipart boundary itself.
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
    });
    return handleResponse(res);
  }

  const { file: _omit, ...json } = version;
  const res = await fetchWithRetry(url, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(json),
  });
  return handleResponse(res);
}

/** Who owes what. Managers see their reports; HR sees everyone. */
export async function fetchHrCompliance(token: string): Promise<HrComplianceMatrix> {
  const res = await fetchWithRetry(`${BASE}/hr/policies-compliance`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/** Person-scoped documents. Scoped server-side by row-level security. */
export async function fetchHrEmployeeDocuments(
  token: string,
  teamMemberId: string,
): Promise<{ documents: HrEmployeeDocument[] }> {
  const res = await fetchWithRetry(
    `${BASE}/hr/people/${encodeURIComponent(teamMemberId)}/documents`,
    { headers: authHeaders(token) },
  );
  return handleResponse(res);
}

export async function uploadHrEmployeeDocument(
  token: string,
  teamMemberId: string,
  file: File,
  opts: { category?: string; visibility?: string; title?: string; retentionUntil?: string } = {},
): Promise<{ document: HrEmployeeDocument }> {
  const form = new FormData();
  form.append('file', file);
  if (opts.category) form.append('category', opts.category);
  if (opts.visibility) form.append('visibility', opts.visibility);
  if (opts.title) form.append('title', opts.title);
  if (opts.retentionUntil) form.append('retentionUntil', opts.retentionUntil);

  const res = await fetchWithRetry(
    `${BASE}/hr/people/${encodeURIComponent(teamMemberId)}/documents`,
    // No Content-Type: the browser sets the multipart boundary itself.
    { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form },
  );
  return handleResponse(res);
}

/** A short-lived (5 minute) download link. Access is re-checked server-side. */
export async function getHrDocumentLink(
  token: string,
  documentId: string,
): Promise<{ url: string | null; expiresInSeconds: number }> {
  const res = await fetchWithRetry(`${BASE}/hr/documents/${encodeURIComponent(documentId)}/link`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ─── HR time off ──────────────────────────────────────────────────

export type HrLeaveType = 'vacation' | 'sick' | 'personal' | 'bereavement' | 'unpaid' | 'comp-time';
export type HrAccrualMethod = 'annual-grant' | 'per-pay-period' | 'hours-worked';
export type HrRequestStatus = 'draft' | 'pending' | 'approved' | 'denied' | 'cancelled';

export interface HrTimeOffPolicy {
  id: string;
  name: string;
  leave_type: HrLeaveType;
  accrual_method: HrAccrualMethod;
  accrual_rate: number;
  accrual_cap: number | null;
  pay_periods_per_year: number | null;
  carryover_cap: number | null;
  carryover_expires_months: number | null;
  waiting_period_days: number;
  negative_balance: 'deny' | 'allow' | 'limit';
  negative_limit: number | null;
  applies_to_employment_types: string[];
  hours_per_day: number;
  /** Weekday numbers (0=Sunday) that are not working days. Empty = every day. */
  non_working_weekdays: number[];
  increment: 'day' | 'half-day' | 'hour';
  requires_approval: boolean;
  is_active: boolean;
}

export interface HrTimeOffBalance {
  policy: HrTimeOffPolicy;
  balance: number;
  earned: number;
  used: number;
  /** Hours committed to pending or future approved leave. */
  pending: number;
  /** balance − pending. What can actually be booked. */
  available: number;
  eligibility: {
    canAccrue: boolean;
    canUse: boolean;
    reason: string | null;
    waitingPeriodEnds: string | null;
  };
}

export interface HrTimeOffRequest {
  id: string;
  team_member_id: string;
  policy_id: string;
  start_date: string;
  end_date: string;
  total_hours: number;
  status: HrRequestStatus;
  reason: string | null;
  approver_team_member_id: string | null;
  decided_at: string | null;
  decision_note: string | null;
  calendar_event_id: string | null;
  created_at: string;
  /** Present on the approvals queue only. */
  memberName?: string;
  canDecide?: boolean;
  time_off_policies?: { name: string; leave_type: HrLeaveType };
}

export interface HrLedgerEntry {
  id: string;
  policy_id: string;
  entry_type:
    | 'accrual' | 'grant' | 'usage' | 'reversal'
    | 'adjustment' | 'carryover' | 'expiry' | 'payout';
  delta_hours: number;
  effective_date: string;
  note: string | null;
  created_at: string;
}

/** Balances, requests and the ledger. Omit teamMemberId for your own. */
export async function fetchHrTimeOff(
  token: string,
  teamMemberId?: string,
): Promise<{
  teamMemberId: string;
  balances: HrTimeOffBalance[];
  requests: HrTimeOffRequest[];
  ledger: HrLedgerEntry[];
}> {
  const qs = teamMemberId ? `?teamMemberId=${encodeURIComponent(teamMemberId)}` : '';
  const res = await fetchWithRetry(`${BASE}/hr/time-off${qs}`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/**
 * File a leave request.
 *
 * Validated against the balance on the START date rather than today's, so
 * accrual between now and then counts. `unrouted` comes back true when no
 * approver could be found — worth surfacing, since such a request would
 * otherwise sit indefinitely.
 */
export async function createHrTimeOffRequest(
  token: string,
  input: {
    policyId: string;
    startDate: string;
    endDate: string;
    totalHours?: number;
    reason?: string;
    isPartialDay?: boolean;
  },
): Promise<{
  request: HrTimeOffRequest;
  warnings: string[];
  routing: { approverTeamMemberId: string | null; route: string };
  unrouted: boolean;
}> {
  const res = await fetchWithRetry(`${BASE}/hr/time-off/requests`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
  });
  return handleResponse(res);
}

/** Approve or deny. Never available on your own request. */
export async function decideHrTimeOffRequest(
  token: string,
  requestId: string,
  decision: 'approve' | 'deny',
  note?: string,
): Promise<{ status: string }> {
  const res = await fetchWithRetry(`${BASE}/hr/time-off/requests/${encodeURIComponent(requestId)}/decide`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify({ decision, note }),
  });
  return handleResponse(res);
}

/** Cancel. Approved hours come back as a reversal entry, never a deletion. */
export async function cancelHrTimeOffRequest(
  token: string,
  requestId: string,
): Promise<{ status: string }> {
  const res = await fetchWithRetry(`${BASE}/hr/time-off/requests/${encodeURIComponent(requestId)}/cancel`, {
    method: 'POST', headers: authHeaders(token),
  });
  return handleResponse(res);
}

/** The approval queue. Managers see their reports; HR sees everyone. */
export async function fetchHrTimeOffApprovals(
  token: string,
): Promise<{ requests: HrTimeOffRequest[] }> {
  const res = await fetchWithRetry(`${BASE}/hr/time-off/approvals`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export async function fetchHrTimeOffPolicies(
  token: string,
): Promise<{ policies: HrTimeOffPolicy[] }> {
  const res = await fetchWithRetry(`${BASE}/hr/time-off/policies`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export async function createHrTimeOffPolicy(
  token: string,
  input: Partial<HrTimeOffPolicy> & { name: string },
): Promise<{ policy: HrTimeOffPolicy }> {
  const res = await fetchWithRetry(`${BASE}/hr/time-off/policies`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
  });
  return handleResponse(res);
}

/** Assign a policy, optionally with an opening balance for a mid-year migration. */
export async function assignHrTimeOffPolicy(
  token: string,
  policyId: string,
  input: { teamMemberId: string; effectiveFrom?: string; openingBalanceHours?: number },
): Promise<{ assignment: unknown }> {
  const res = await fetchWithRetry(
    `${BASE}/hr/time-off/policies/${encodeURIComponent(policyId)}/assign`,
    { method: 'POST', headers: authHeaders(token), body: JSON.stringify(input) },
  );
  return handleResponse(res);
}

// ─── HR onboarding & offboarding ──────────────────────────────────

export type HrWorkflowKind = 'onboarding' | 'offboarding';
export type HrAssigneeRole = 'employee' | 'manager' | 'hr' | 'it' | 'facilities';
export type HrTaskStatus = 'pending' | 'done' | 'skipped' | 'blocked';

export interface HrWorkflowTemplateTask {
  id?: string;
  title: string;
  description?: string | null;
  assignee_role: HrAssigneeRole;
  /** Days from the anchor. Negative is normal — most onboarding is pre-start. */
  relative_days: number;
  anchor: 'hire_date' | 'last_day';
  required_document_category?: string | null;
  is_required: boolean;
  sort_order: number;
}

export interface HrWorkflowTemplate {
  id: string;
  kind: HrWorkflowKind;
  name: string;
  description: string | null;
  applies_to_employment_types: string[];
  is_active: boolean;
  tasks: HrWorkflowTemplateTask[];
}

export interface HrWorkflowInstance {
  id: string;
  team_member_id: string;
  kind: HrWorkflowKind;
  anchor_date: string;
  status: 'active' | 'completed' | 'cancelled';
  template_name: string | null;
  started_at: string;
  completed_at: string | null;
}

export interface HrWorkflowTask {
  id: string;
  instance_id: string;
  title: string;
  description: string | null;
  assignee_role: HrAssigneeRole;
  assignee_team_member_id: string | null;
  assigneeName?: string | null;
  due_date: string | null;
  status: HrTaskStatus;
  is_required: boolean;
  required_document_category: string | null;
  skip_reason: string | null;
  sort_order: number;
  /** Present on the "assigned to me" list. */
  subjectName?: string | null;
  kind?: HrWorkflowKind;
}

export interface HrWorkflowProgress {
  total: number;
  done: number;
  skipped: number;
  pending: number;
  overdue: number;
  /** Required, pending, and assigned to nobody — these never get done. */
  unassigned: number;
  percentComplete: number;
  canComplete: boolean;
  blockers: string[];
}

export interface HrOffboardingReview {
  leaveBalances: Array<{ policyId: string; policyName: string; hours: number }>;
  totalUnusedHours: number;
  /** Approved leave starting after the last day, which will not happen. */
  futureApprovedRequestIds: string[];
  outstandingTasks: string[];
  lastDay: string;
  note: string;
}

export async function fetchHrWorkflowTemplates(token: string): Promise<{
  templates: HrWorkflowTemplate[];
  starters: {
    onboarding: HrWorkflowTemplateTask[];
    offboarding: HrWorkflowTemplateTask[];
  };
}> {
  const res = await fetchWithRetry(`${BASE}/hr/workflows/templates`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export async function createHrWorkflowTemplate(
  token: string,
  input: {
    kind: HrWorkflowKind;
    name: string;
    description?: string;
    applies_to_employment_types?: string[];
    tasks?: HrWorkflowTemplateTask[];
  },
): Promise<{ template: HrWorkflowTemplate; taskCount: number }> {
  const res = await fetchWithRetry(`${BASE}/hr/workflows/templates`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
  });
  return handleResponse(res);
}

/**
 * Start a checklist for someone.
 *
 * `anchorDate` is their start date for onboarding, or their last day for
 * offboarding — every task's due date is measured from it.
 */
export async function startHrWorkflow(
  token: string,
  input: { templateId: string; teamMemberId: string; anchorDate: string },
): Promise<{ instance: HrWorkflowInstance; taskCount: number; unassignedCount: number }> {
  const res = await fetchWithRetry(`${BASE}/hr/workflows/start`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
  });
  return handleResponse(res);
}

export async function fetchHrWorkflow(token: string, instanceId: string): Promise<{
  instance: HrWorkflowInstance;
  memberName: string;
  tasks: HrWorkflowTask[];
  progress: HrWorkflowProgress;
  viewerCan: { manage: boolean };
}> {
  const res = await fetchWithRetry(`${BASE}/hr/workflows/${encodeURIComponent(instanceId)}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

/** Checklist tasks assigned to the caller, across every active workflow. */
export async function fetchMyHrWorkflowTasks(
  token: string,
): Promise<{ tasks: HrWorkflowTask[]; overdueCount: number }> {
  const res = await fetchWithRetry(`${BASE}/hr/workflows/tasks/mine`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/** Tick a task off, or skip it with a stated reason. */
export async function updateHrWorkflowTask(
  token: string,
  taskId: string,
  input: { status: 'done' | 'skipped'; skipReason?: string; documentId?: string },
): Promise<{ task: HrWorkflowTask }> {
  const res = await fetchWithRetry(`${BASE}/hr/workflows/tasks/${encodeURIComponent(taskId)}`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
  });
  return handleResponse(res);
}

/** Close a checklist. Offboarding also takes the person off the active roster. */
export async function completeHrWorkflow(
  token: string,
  instanceId: string,
): Promise<{ status: string; deactivatedRoster: boolean }> {
  const res = await fetchWithRetry(`${BASE}/hr/workflows/${encodeURIComponent(instanceId)}/complete`, {
    method: 'POST', headers: authHeaders(token),
  });
  return handleResponse(res);
}

/**
 * What a departure leaves unresolved.
 *
 * Reports only. Unused leave is shown, never paid out — whether it must be paid
 * depends on state law and the employment agreement.
 */
export async function fetchHrOffboardingReview(
  token: string,
  teamMemberId: string,
  lastDay?: string,
): Promise<HrOffboardingReview> {
  const qs = lastDay ? `?lastDay=${encodeURIComponent(lastDay)}` : '';
  const res = await fetchWithRetry(
    `${BASE}/hr/workflows/offboarding-review/${encodeURIComponent(teamMemberId)}${qs}`,
    { headers: authHeaders(token) },
  );
  return handleResponse(res);
}

export async function fetchHrPersonWorkflows(
  token: string,
  teamMemberId: string,
): Promise<{ instances: HrWorkflowInstance[] }> {
  const res = await fetchWithRetry(
    `${BASE}/hr/people/${encodeURIComponent(teamMemberId)}/workflows`,
    { headers: authHeaders(token) },
  );
  return handleResponse(res);
}

// ─── HR certifications & training ─────────────────────────────────

export type HrExpiryState =
  | 'does-not-expire' | 'current' | 'expiring-soon' | 'expired' | 'unverified' | 'inactive';

export type HrGapReason = 'never-held' | 'expired' | 'expiring-soon' | 'unverified';

export interface HrCertificationType {
  id: string;
  name: string;
  issuing_body: string | null;
  description: string | null;
  /** Null means it does not expire. */
  validity_months: number | null;
  required_for_department_ids: string[];
  required_for_employment_types: string[];
  /** Drives the broadly-visible safety badge. Reserve it for things that hurt people. */
  is_safety_critical: boolean;
  is_active: boolean;
}

export interface HrCertificationState {
  state: HrExpiryState;
  daysRemaining: number | null;
  expiresOn: string | null;
  needsAttention: boolean;
}

export interface HrEmployeeCertification {
  id: string;
  team_member_id: string;
  certification_type_id: string;
  issued_date: string | null;
  expires_date: string | null;
  certificate_number: string | null;
  document_id: string | null;
  verified_by: string | null;
  verified_at: string | null;
  status: 'active' | 'superseded' | 'revoked';
  notes: string | null;
  type: HrCertificationType | null;
  state: HrCertificationState;
}

export interface HrTrainingRecord {
  id: string;
  team_member_id: string;
  title: string;
  provider: string | null;
  completed_date: string;
  hours: number | null;
  notes: string | null;
}

export interface HrCertificationGap {
  teamMemberId: string;
  memberName: string;
  certificationTypeId: string;
  certificationName: string;
  reason: HrGapReason;
  isSafetyCritical: boolean;
  expiresOn: string | null;
  daysRemaining: number | null;
}

/**
 * The broadly-readable safety signal.
 *
 * Deliberately narrow: member ids and lapsed certification names only, no
 * certificate detail. Anyone deciding who goes up a ladder needs this; the
 * record behind it stays need-to-know.
 */
export interface HrSafetyStatus {
  teamMemberId: string;
  hasExpired: boolean;
  hasExpiringSoon: boolean;
  expiredNames: string[];
}

export async function fetchHrCertificationTypes(token: string): Promise<{
  types: HrCertificationType[];
  starters: Array<Omit<HrCertificationType, 'id'> & { description: string }>;
}> {
  const res = await fetchWithRetry(`${BASE}/hr/certification-types`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export async function createHrCertificationType(
  token: string,
  input: Partial<HrCertificationType> & { name: string },
): Promise<{ type: HrCertificationType }> {
  const res = await fetchWithRetry(`${BASE}/hr/certification-types`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
  });
  return handleResponse(res);
}

export async function updateHrCertificationType(
  token: string,
  typeId: string,
  patch: Partial<HrCertificationType>,
): Promise<{ type: HrCertificationType }> {
  const res = await fetchWithRetry(`${BASE}/hr/certification-types/${encodeURIComponent(typeId)}`, {
    method: 'PUT', headers: authHeaders(token), body: JSON.stringify(patch),
  });
  return handleResponse(res);
}

export async function fetchHrCertifications(
  token: string,
  teamMemberId: string,
): Promise<{ certifications: HrEmployeeCertification[]; training: HrTrainingRecord[] }> {
  const res = await fetchWithRetry(
    `${BASE}/hr/people/${encodeURIComponent(teamMemberId)}/certifications`,
    { headers: authHeaders(token) },
  );
  return handleResponse(res);
}

/**
 * Record a certification. HR admins only.
 *
 * Pass `verified: true` only when someone has actually seen the card —
 * recording and verifying are separate facts, and only the second is worth
 * relying on when deciding who works at height.
 */
export async function createHrCertification(
  token: string,
  teamMemberId: string,
  input: {
    certification_type_id: string;
    issued_date?: string;
    expires_date?: string;
    certificate_number?: string;
    verified?: boolean;
    supersedesId?: string;
    notes?: string;
  },
): Promise<{ certification: HrEmployeeCertification; suggestedExpiry: string | null }> {
  const res = await fetchWithRetry(
    `${BASE}/hr/people/${encodeURIComponent(teamMemberId)}/certifications`,
    { method: 'POST', headers: authHeaders(token), body: JSON.stringify(input) },
  );
  return handleResponse(res);
}

export async function verifyHrCertification(
  token: string,
  certId: string,
): Promise<{ certification: HrEmployeeCertification }> {
  const res = await fetchWithRetry(`${BASE}/hr/certifications/${encodeURIComponent(certId)}/verify`, {
    method: 'POST', headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function createHrTrainingRecord(
  token: string,
  teamMemberId: string,
  input: { title: string; completed_date: string; provider?: string; hours?: number },
): Promise<{ training: HrTrainingRecord }> {
  const res = await fetchWithRetry(
    `${BASE}/hr/people/${encodeURIComponent(teamMemberId)}/training`,
    { method: 'POST', headers: authHeaders(token), body: JSON.stringify(input) },
  );
  return handleResponse(res);
}

/**
 * Who is required to hold something and does not.
 *
 * Includes people with no record at all — the case a report built from the
 * certificates table structurally cannot see.
 */
export async function fetchHrCertificationGaps(token: string): Promise<{
  gaps: HrCertificationGap[];
  safetyCriticalCount: number;
  peopleAffected: number;
}> {
  const res = await fetchWithRetry(`${BASE}/hr/certifications/gaps`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/** Readable by every signed-in member — see HrSafetyStatus. */
export async function fetchHrSafetyStatus(
  token: string,
): Promise<{ statuses: HrSafetyStatus[]; expiredCount: number }> {
  const res = await fetchWithRetry(`${BASE}/hr/certifications/safety-status`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ─── HR performance: 1:1s, goals, reviews ─────────────────────────
//
// The share gate is visible in these types on purpose. A review carries
// `shared_with_employee_at` and an `access` field, and the list endpoint omits
// rows the caller may not see entirely rather than returning a redacted shell —
// so an unshared review is not merely hidden in the UI, it never arrives.

export type HrReviewAccess = 'author' | 'reader';
export type HrCycleState = 'draft' | 'upcoming' | 'open' | 'overdue' | 'closed';
export type HrGoalHealth =
  | 'not-started' | 'on-track' | 'at-risk' | 'overdue' | 'done' | 'closed';

export interface HrGoalState {
  health: HrGoalHealth;
  daysRemaining: number | null;
  /** Where a steady rate would have reached by now. Null when it cannot be told. */
  expectedProgress: number | null;
}

export interface HrGoal {
  id: string;
  team_member_id: string;
  title: string;
  description: string | null;
  target_date: string | null;
  status: 'draft' | 'active' | 'achieved' | 'missed' | 'cancelled';
  progress: number;
  project_id: string | null;
  parent_goal_id: string | null;
  created_at: string;
  state: HrGoalState;
}

export interface HrOneOnOne {
  id: string;
  employee_team_member_id: string;
  manager_team_member_id: string;
  scheduled_date: string;
  shared_notes: string | null;
  /**
   * Absent for the employee — stripped server-side, not hidden client-side.
   * Named honestly rather than "private": anything in an HR system is
   * discoverable in a dispute.
   */
  manager_notes?: string | null;
  status: 'scheduled' | 'held' | 'skipped';
  calendar_event_id: string | null;
}

export interface HrOneOnOneItem {
  id: string;
  employee_team_member_id: string;
  manager_team_member_id: string;
  text: string;
  raised_by: string;
  kind: 'topic' | 'question' | 'feedback' | 'blocker' | 'follow-up';
  one_on_one_id: string | null;
  /** Meetings this was slotted into but not covered. Length is the carry count. */
  previous_one_on_one_ids: string[];
  status: 'open' | 'discussed' | 'resolved';
  outcome: string | null;
}

export interface HrReviewQuestion {
  key: string;
  prompt: string;
  type?: string;
}

export interface HrReviewCycle {
  id: string;
  name: string;
  period_start: string;
  period_end: string;
  due_date: string | null;
  status: 'draft' | 'open' | 'closed';
  template: HrReviewQuestion[];
  applies_to_employment_types: string[];
  state: HrCycleState;
}

export interface HrReview {
  id: string;
  cycle_id: string;
  employee_team_member_id: string;
  reviewer_team_member_id: string;
  reviewer_type: 'self' | 'manager' | 'peer';
  responses: Record<string, string>;
  status: 'draft' | 'submitted';
  submitted_at: string | null;
  /** Null means the employee cannot see this review, whatever its status. */
  shared_with_employee_at: string | null;
  access: HrReviewAccess;
  canEdit: boolean;
  canShare: boolean;
}

export interface HrCycleProgress {
  cycle: HrReviewCycle;
  expected: number;
  submitted: number;
  percentComplete: number;
  outstanding: Array<{
    employeeTeamMemberId: string;
    employeeName: string;
    reviewerTeamMemberId: string;
    reviewerName: string;
    reviewerType: 'self' | 'manager' | 'peer';
  }>;
  /** Every participant's name, including those who have already submitted. */
  names: Record<string, string>;
}

export async function fetchHrGoals(
  token: string,
  teamMemberId: string,
): Promise<{ goals: HrGoal[] }> {
  const res = await fetchWithRetry(`${BASE}/hr/people/${teamMemberId}/goals`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function createHrGoal(
  token: string,
  teamMemberId: string,
  goal: Partial<HrGoal>,
): Promise<{ goal: HrGoal }> {
  const res = await fetchWithRetry(`${BASE}/hr/people/${teamMemberId}/goals`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(goal),
  });
  return handleResponse(res);
}

export async function updateHrGoal(
  token: string,
  goalId: string,
  patch: Partial<HrGoal>,
): Promise<{ goal: HrGoal }> {
  const res = await fetchWithRetry(`${BASE}/hr/goals/${goalId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(patch),
  });
  return handleResponse(res);
}

export async function deleteHrGoal(token: string, goalId: string): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/hr/goals/${goalId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function fetchHrOneOnOnes(
  token: string,
  withMemberId?: string,
): Promise<{ meetings: HrOneOnOne[]; items: HrOneOnOneItem[]; carryOver: HrOneOnOneItem[] }> {
  const qs = withMemberId ? `?with=${encodeURIComponent(withMemberId)}` : '';
  const res = await fetchWithRetry(`${BASE}/hr/one-on-ones${qs}`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export async function createHrOneOnOne(
  token: string,
  meeting: Partial<HrOneOnOne>,
): Promise<{ meeting: HrOneOnOne }> {
  const res = await fetchWithRetry(`${BASE}/hr/one-on-ones`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(meeting),
  });
  return handleResponse(res);
}

export async function updateHrOneOnOne(
  token: string,
  meetingId: string,
  patch: Partial<HrOneOnOne>,
): Promise<{ meeting: HrOneOnOne }> {
  const res = await fetchWithRetry(`${BASE}/hr/one-on-ones/${meetingId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(patch),
  });
  return handleResponse(res);
}

export async function createHrOneOnOneItem(
  token: string,
  item: Partial<HrOneOnOneItem>,
): Promise<{ item: HrOneOnOneItem }> {
  const res = await fetchWithRetry(`${BASE}/hr/one-on-one-items`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(item),
  });
  return handleResponse(res);
}

export async function updateHrOneOnOneItem(
  token: string,
  itemId: string,
  patch: Partial<HrOneOnOneItem> & { one_on_one_id?: string },
): Promise<{ item: HrOneOnOneItem }> {
  const res = await fetchWithRetry(`${BASE}/hr/one-on-one-items/${itemId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(patch),
  });
  return handleResponse(res);
}

export async function deleteHrOneOnOneItem(
  token: string,
  itemId: string,
): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/hr/one-on-one-items/${itemId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function fetchHrReviewCycles(token: string): Promise<{
  cycles: HrReviewCycle[];
  defaultTemplate: HrReviewQuestion[];
}> {
  const res = await fetchWithRetry(`${BASE}/hr/review-cycles`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export async function createHrReviewCycle(
  token: string,
  cycle: Partial<HrReviewCycle>,
): Promise<{ cycle: HrReviewCycle }> {
  const res = await fetchWithRetry(`${BASE}/hr/review-cycles`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(cycle),
  });
  return handleResponse(res);
}

export async function updateHrReviewCycle(
  token: string,
  cycleId: string,
  patch: Partial<HrReviewCycle>,
): Promise<{ cycle: HrReviewCycle }> {
  const res = await fetchWithRetry(`${BASE}/hr/review-cycles/${cycleId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(patch),
  });
  return handleResponse(res);
}

export async function fetchHrCycleProgress(
  token: string,
  cycleId: string,
): Promise<HrCycleProgress> {
  const res = await fetchWithRetry(`${BASE}/hr/review-cycles/${cycleId}/progress`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function fetchHrReviews(
  token: string,
  opts: { cycleId?: string; about?: string } = {},
): Promise<{ reviews: HrReview[] }> {
  const params = new URLSearchParams();
  if (opts.cycleId) params.set('cycleId', opts.cycleId);
  if (opts.about) params.set('about', opts.about);
  const qs = params.toString() ? `?${params}` : '';
  const res = await fetchWithRetry(`${BASE}/hr/reviews${qs}`, { headers: authHeaders(token) });
  return handleResponse(res);
}

export async function createHrReview(
  token: string,
  review: { cycle_id: string; employee_team_member_id: string; reviewer_type?: string },
): Promise<{ review: HrReview }> {
  const res = await fetchWithRetry(`${BASE}/hr/reviews`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(review),
  });
  return handleResponse(res);
}

export async function updateHrReview(
  token: string,
  reviewId: string,
  patch: { responses?: Record<string, string>; status?: 'submitted' },
): Promise<{ review: HrReview }> {
  const res = await fetchWithRetry(`${BASE}/hr/reviews/${reviewId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(patch),
  });
  return handleResponse(res);
}

/**
 * Release a review to the person it is about.
 *
 * Its own call rather than a flag on updateHrReview, because it is the one
 * irreversible act here: once somebody has read a review about themselves,
 * un-sharing it changes nothing.
 */
export async function shareHrReview(
  token: string,
  reviewId: string,
): Promise<{ review: HrReview }> {
  const res = await fetchWithRetry(`${BASE}/hr/reviews/${reviewId}/share`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ─── HR payroll sync ──────────────────────────────────────────────
//
// StageFlow does not calculate pay. These endpoints move approved leave and
// worked hours OUT as files, and read balances back IN for comparison. The
// ownership split behind every shape here: payroll owns balances and
// compensation, StageFlow owns requests and approvals.

export type HrPayrollProviderId = 'manual-csv' | 'finch' | 'onpay-direct';
export type HrMatchMethod = 'email' | 'manual' | 'name-fuzzy';
export type HrDriftSeverity = 'match' | 'minor' | 'material';

export interface HrPayrollProvider {
  id: HrPayrollProviderId;
  label: string;
  capabilities: {
    readDirectory: boolean;
    readPayStatements: boolean;
    readTimeOffBalances: boolean;
    writeTimeOff: boolean;
    /** False for every provider today — show a last-synced time, not "live". */
    realtime: boolean;
  };
  setup: string;
  credentialKey: string | null;
  available: boolean;
}

export interface HrPayrollLink {
  id: string;
  provider: HrPayrollProviderId;
  external_id: string;
  team_member_id: string;
  memberName: string;
  external_name: string | null;
  external_email: string | null;
  match_method: HrMatchMethod;
  confidence: number | null;
  linked_at: string;
}

export interface HrPayrollRun {
  id: string;
  provider: string;
  direction: 'import' | 'export';
  kind: 'directory' | 'balances' | 'time-off' | 'timesheet-hours';
  /** 'partial' is normal, not a failure — some rows needed a human. */
  status: 'running' | 'succeeded' | 'partial' | 'failed';
  rows_total: number;
  rows_applied: number;
  rows_review: number;
  rows_error: number;
  file_name: string | null;
  started_at: string;
  finished_at: string | null;
}

export interface HrMatchProposal {
  externalId: string;
  externalName: string;
  externalEmail: string | null;
  teamMemberId: string | null;
  method: HrMatchMethod | null;
  confidence: number;
  alternatives?: string[];
  reason: string;
}

export interface HrMatchOutcome {
  /** Email matches. Written automatically. */
  autoLink: HrMatchProposal[];
  /** Name matches and ambiguities. NEVER written without a person confirming. */
  needsReview: HrMatchProposal[];
  unmatched: HrMatchProposal[];
  alreadyLinked: string[];
}

export interface HrPayrollOverview {
  providers: HrPayrollProvider[];
  links: HrPayrollLink[];
  runs: HrPayrollRun[];
  needsReviewCount: number;
  unlinkedMembers: Array<{ teamMemberId: string; name: string; email: string | null }>;
}

export interface HrDriftEntry {
  teamMemberId: string;
  memberName: string;
  policyId: string | null;
  policyLabel: string;
  stageflowHours: number;
  payrollHours: number;
  /** payroll minus StageFlow. Positive means payroll thinks they have more. */
  differenceHours: number;
  asOfDate: string;
  severity: HrDriftSeverity;
}

export interface HrPayrollExport {
  format: 'csv';
  fileName: string;
  content: string;
  rowCount: number;
  /** People left out because they have no payroll link — never silent. */
  skipped: Array<{ teamMemberId: string; memberName: string; reason: string }>;
  note?: string;
}

export async function fetchHrPayroll(token: string): Promise<HrPayrollOverview> {
  const res = await fetchWithRetry(`${BASE}/hr/payroll`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/** Preview a directory import without writing anything. */
export async function previewHrPayrollDirectory(
  token: string,
  file: File,
  provider: HrPayrollProviderId = 'manual-csv',
): Promise<{ dryRun: true; outcome: HrMatchOutcome; problems: Array<{ rowNumber: number; message: string }>; employeeCount: number }> {
  const form = new FormData();
  form.append('file', file);
  form.append('provider', provider);
  form.append('dryRun', 'true');
  const res = await fetchWithRetry(`${BASE}/hr/payroll/import/directory`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
  });
  return handleResponse(res);
}

export async function importHrPayrollDirectory(
  token: string,
  file: File,
  provider: HrPayrollProviderId = 'manual-csv',
): Promise<{ runId: string; outcome: HrMatchOutcome; problems: Array<{ rowNumber: number; message: string }>; counts: Record<string, number> }> {
  const form = new FormData();
  form.append('file', file);
  form.append('provider', provider);
  const res = await fetchWithRetry(`${BASE}/hr/payroll/import/directory`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
  });
  return handleResponse(res);
}

export async function importHrPayrollBalances(
  token: string,
  file: File,
  provider: HrPayrollProviderId = 'manual-csv',
): Promise<{ runId: string; counts: Record<string, number>; problems: Array<{ rowNumber: number; message: string }> }> {
  const form = new FormData();
  form.append('file', file);
  form.append('provider', provider);
  const res = await fetchWithRetry(`${BASE}/hr/payroll/import/balances`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
  });
  return handleResponse(res);
}

export async function fetchHrPayrollDrift(token: string): Promise<{
  drift: HrDriftEntry[];
  materialCount: number;
  unmappedPolicies: string[];
}> {
  const res = await fetchWithRetry(`${BASE}/hr/payroll/drift`, { headers: authHeaders(token) });
  return handleResponse(res);
}

/**
 * Reconcile ONE drift entry.
 *
 * Writes an adjustment ledger row rather than overwriting a balance, and there
 * is deliberately no bulk version: accepting payroll's numbers wholesale is
 * how a payroll bug silently rewrites forty balances.
 */
export async function reconcileHrPayrollDrift(
  token: string,
  drift: HrDriftEntry,
): Promise<{ adjusted: number }> {
  const res = await fetchWithRetry(`${BASE}/hr/payroll/drift/reconcile`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify({ drift }),
  });
  return handleResponse(res);
}

export async function linkHrPayrollEmployee(
  token: string,
  input: {
    provider: HrPayrollProviderId;
    externalId: string;
    teamMemberId: string;
    externalName?: string | null;
    externalEmail?: string | null;
    rowId?: string;
  },
): Promise<{ link: HrPayrollLink }> {
  const res = await fetchWithRetry(`${BASE}/hr/payroll/links`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify(input),
  });
  return handleResponse(res);
}

export async function unlinkHrPayrollEmployee(
  token: string,
  linkId: string,
): Promise<{ success: boolean }> {
  const res = await fetchWithRetry(`${BASE}/hr/payroll/links/${encodeURIComponent(linkId)}`, {
    method: 'DELETE', headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function exportHrPayrollTimeOff(
  token: string,
  range: { from?: string; to?: string } = {},
): Promise<HrPayrollExport> {
  const params = new URLSearchParams();
  if (range.from) params.set('from', range.from);
  if (range.to) params.set('to', range.to);
  const qs = params.toString() ? `?${params}` : '';
  const res = await fetchWithRetry(`${BASE}/hr/payroll/export/time-off${qs}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function exportHrPayrollHours(
  token: string,
  range: { from?: string; to?: string } = {},
): Promise<HrPayrollExport> {
  const params = new URLSearchParams();
  if (range.from) params.set('from', range.from);
  if (range.to) params.set('to', range.to);
  const qs = params.toString() ? `?${params}` : '';
  const res = await fetchWithRetry(`${BASE}/hr/payroll/export/hours${qs}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

// ============================================
// PRODUCT FEEDBACK (TicketCommand portal relay)
// ============================================
// The server forwards these to the central sivart control center — see
// supabase/functions/server/portal-relay.ts. The routes live at the function
// root rather than under /api, so they get their own base.

const FEEDBACK_BASE = BASE.replace(/\/api$/, '');

export type FeedbackKind = 'bug' | 'feature' | 'feedback';
export type FeedbackSeverity = 'low' | 'medium' | 'high';

export interface FeedbackPayload {
  kind: FeedbackKind;
  severity: FeedbackSeverity;
  title?: string;
  body: string;
  path: string;
  user_agent: string;
  app_version: string;
  // Client-asserted identity — the server prefers the verified session token
  // (passed as `token` below) and only falls back to these, marked unverified.
  submitter_email?: string;
  submitter_name?: string;
}

// Call sites destructure `accessToken` from useAuth(); when it's null the
// request rides the anon key and the server records the submitter as
// client-asserted rather than verified.
export async function submitFeedback(
  token: string | null,
  payload: FeedbackPayload,
): Promise<{ success: boolean; id?: string }> {
  const res = await fetchWithRetry(`${FEEDBACK_BASE}/feedback`, {
    method: 'POST',
    headers: token ? authHeaders(token) : headers(),
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// Fire-and-forget client-error report; also relayed to the portal. Never
// throws — the error reporter must not create errors of its own.
export async function reportClientError(
  token: string | null,
  payload: {
    source: string;
    message: string;
    stack?: string;
    component_stack?: string;
    url?: string;
    user_agent?: string;
    app_version?: string;
    user_email?: string;
    user_id?: string;
  },
): Promise<void> {
  try {
    await fetch(`${FEEDBACK_BASE}/client-errors`, {
      method: 'POST',
      headers: token ? authHeaders(token) : headers(),
      body: JSON.stringify(payload),
    });
  } catch {
    // Swallow — nothing sensible to do if the reporter itself fails.
  }
}
